import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PageHeader from '@/components/Layout/PageHeader';
import BydaUsageSummary from '@/components/BydaUsageSummary';
import BydaResultFileList from '@/components/BydaResultFileList';
import BydaResultPdfPreview from '@/components/BydaResultPdfPreview';
import BydaYvwSewerInfoDialog from '@/components/BydaYvwSewerInfoDialog';
import IfcAdminUploadPanel from '@/components/ifc/IfcAdminUploadPanel';
import ProjectFilesAdminPanel from '@/components/projectFiles/ProjectFilesAdminPanel';
import { useAuth } from '@/components/Auth/AuthContext';
import { azureSqlService } from '@/services/azureSqlService.js';
import { matchAddressToSuburbList, type AddressSuburbMatch } from '@/services/suburbLookup';
import { getPrimaryRoleLabel, hasAdminEquivalentAccess, hasRequiredRoleAccess } from '@/utils/swaRole';
import { formatSuburbLabel } from '@/utils/suburbText';
import {
  BYDA_WORKPLACE_OPTIONS,
  buildBydaDraftPolygon,
  createBydaDraftFromProject,
  getDefaultBydaDesignActivityCodes,
  mapWorkplaceSelectionsToPayload,
  normalizeDraftField,
  syncBydaCodes,
  toggleBydaCode,
  tryBuildPolygonFromDraftPoints,
  type BydaDraftForm,
  type BydaPointDraft,
} from '@/utils/bydaDraft';
import { formatBydaDisplayDate } from '@/utils/bydaDates';
import {
  Button,
  Card,
  CardHeader,
  EmptyState,
  FormSection,
  PageSection,
  SearchInput,
  Select,
  StatusBadge,
  Textarea,
} from '@/ui/components';
import {
  CompanyAiChatResult,
  CompanyAiConversationDetail,
  CompanyAiConversationSummary,
  CompanyAiFilePreview,
  CompanyAiGroundingPreview,
  CompanyAiKnowledgeFile,
  CompanyAiKnowledgeSource,
  CompanyAiOnlineQuoteFile,
  CompanyAiOnlineQuoteFileGroup,
  CompanyAiQueryResult,
  CompanyAiSearchResult,
  CompanyAiUsage,
  companyAiService,
} from '@/services/companyAiService';
import {
  bydaService,
  type BydaEnquiryDetail,
  type BydaFormOption,
  type BydaFormOptions,
  type BydaResultDocument,
  type BydaUsagePolicySummary,
  type BydaWorkspaceEnquiryRecord,
  type BydaYvwSewerInfo,
  type CreateBydaEnquiryInput,
} from '@/services/bydaService';
import { buildBydaBlockedMessage, buildBydaConfirmationMessage } from '@/utils/bydaPolicy';
import { readCompanyAiFollowUpSeed } from '@/utils/companyAiNavigation';
import { parseStructuredAnswerBody, type StructuredAnswerBlock } from '@/utils/listAnswerHelper';
import CompanyAiNavigationTabs, { getCompanyAiWorkspaceTabFromSearch, type CompanyAiWorkspaceTabId } from './CompanyAiNavigationTabs';
import BydaMapCapture from '@/pages/Projects/BydaMapCapture';
import GraphRagVisualizer from './GraphRagVisualizer';
import PmAgentPanel from './PmAgentPanel';
import ReconcilePage from './ReconcilePage';
import FinancePortalPage from './FinancePortalPage';
import OnlineQuoteFilePreview from './OnlineQuoteFilePreview';
import './companyAi.css';

type ChatSourceScope = 'company' | 'project' | 'selected';

function formatDateTime(value?: string | null) {
  if (!value) return 'Just now';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Just now';
  return date.toLocaleString('en-AU', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatPercent(value: number) {
  return `${Math.round(value * 100)}%`;
}

function formatFileSize(value?: number | null) {
  const bytes = Number(value || 0);
  if (!Number.isFinite(bytes) || bytes <= 0) return 'Unknown size';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(kb >= 10 ? 0 : 1)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(mb >= 10 ? 0 : 1)} MB`;
}

function formatBydaStatus(value?: string | null) {
  const normalized = String(value || '').trim();
  if (!normalized) return 'Unknown';
  return normalized
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

const bydaInputStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: '100%',
  padding: '10px 12px',
  borderRadius: '10px',
  border: '1px solid var(--color-border)',
  fontSize: '14px',
  lineHeight: 1.4,
  color: 'var(--color-text)',
  boxSizing: 'border-box',
  backgroundColor: 'var(--color-surface)',
};

const bydaTextareaStyle: React.CSSProperties = {
  ...bydaInputStyle,
  minHeight: '96px',
  resize: 'vertical',
  fontFamily: 'inherit',
};

const bydaOptionGridStyle: React.CSSProperties = {
  display: 'grid',
  gap: '10px',
  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
};

const bydaOptionCardStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '10px 12px',
  borderRadius: '10px',
  border: '1px solid var(--color-border)',
  background: 'var(--color-surface)',
  fontSize: '14px',
};

function formatBydaOptionLabel(option: BydaFormOption) {
  return option.label || option.code;
}

function hasBydaSelection(values: string[], code: string) {
  return values.includes(code);
}

type WorkspaceProjectOption = {
  projectId: number;
  projectCode: string;
  projectName: string;
  siteAddress: string;
  suburb: string;
  postCode: string;
  description: string;
};

function buildProjectSearchValue(project: Pick<WorkspaceProjectOption, 'projectCode' | 'projectName'>) {
  return [project.projectCode, project.projectName].filter(Boolean).join(' - ');
}

type OnlineQuoteGroupHeaderFields = Pick<
  CompanyAiOnlineQuoteFileGroup,
  | 'requestCode'
  | 'referralLeadId'
  | 'uploadUserName'
  | 'uploadUserEmail'
  | 'siteAddress'
  | 'clientName'
  | 'clientEmail'
  | 'projectAddress'
  | 'suburb'
  | 'postCode'
  | 'portalUserEmail'
>;

function pushUniqueOnlineQuoteLabel(parts: string[], value: unknown) {
  const trimmed = String(value || '').trim();
  if (!trimmed) return;
  const normalized = trimmed.toLowerCase();
  if (parts.some((part) => part.toLowerCase() === normalized)) return;
  parts.push(trimmed);
}

function buildOnlineQuoteGroupTitle(group: OnlineQuoteGroupHeaderFields) {
  const parts: string[] = [];
  pushUniqueOnlineQuoteLabel(parts, group.uploadUserName || group.clientName);
  pushUniqueOnlineQuoteLabel(parts, group.uploadUserEmail || group.clientEmail || group.portalUserEmail);
  return parts.join(' - ') || group.requestCode || `Request ${group.referralLeadId}`;
}

function buildOnlineQuoteSiteAddress(group: OnlineQuoteGroupHeaderFields, fallback = 'No address recorded') {
  const parts: string[] = [];
  pushUniqueOnlineQuoteLabel(parts, group.siteAddress || group.projectAddress);
  pushUniqueOnlineQuoteLabel(parts, group.suburb);
  pushUniqueOnlineQuoteLabel(parts, group.postCode);
  return parts.join(', ') || fallback;
}

function getFileExtension(value: unknown) {
  const normalized = String(value || '').trim().toLowerCase().split(/[?#]/, 1)[0];
  const match = normalized.match(/(\.[a-z0-9]+)$/);
  return match?.[1] || '';
}

function isHeicOnlineQuoteFile(file: Pick<CompanyAiOnlineQuoteFile, 'fileName' | 'contentType'>) {
  const contentType = normalizeComparableText(file.contentType);
  return contentType === 'image/heic'
    || contentType === 'image/heif'
    || contentType === 'image/heic-sequence'
    || contentType === 'image/heif-sequence'
    || getFileExtension(file.fileName) === '.heic'
    || getFileExtension(file.fileName) === '.heif';
}

function getOnlineQuoteFileActionLabel(file: Pick<CompanyAiOnlineQuoteFile, 'fileName' | 'contentType'>) {
  return isHeicOnlineQuoteFile(file) ? 'Download HEIC' : 'Download File';
}

function normalizeComparableText(value: unknown) {
  return String(value || '').trim().toLowerCase();
}

function isQuarantinedOnlineQuoteGroup(group: Pick<CompanyAiOnlineQuoteFileGroup, 'referralStatus' | 'reviewStatus'>) {
  const statuses = [group.referralStatus, group.reviewStatus].map(normalizeComparableText);
  return statuses.some((status) => status === 'quarantined' || status === 'rejected_spam');
}

function toOnlineQuoteText(value: unknown) {
  return String(value || '').replace(/\s+/g, ' ').trim();
}

function mapOnlineQuoteProjectScope(group: Pick<CompanyAiOnlineQuoteFileGroup, 'helpType' | 'projectType'>) {
  const helpType = normalizeComparableText(group.helpType);
  const projectType = normalizeComparableText(group.projectType);
  const combined = `${helpType} ${projectType}`;

  if (/inspection/.test(combined)) return 'Site Inspection';
  if (/certif/.test(combined)) return 'Third Party Certification';
  if (/stormwater|storm water|drainage report/.test(combined)) return 'Civil Engineering Stormwater Management Report';
  if (/structural.*civil|civil.*structural/.test(combined)) return 'Structural and Civil Engineering Design and Documentation';
  if (/civil|drainage/.test(combined)) return 'Civil Engineering Design and Documentation';
  return 'Structural Engineering Design and Documentation';
}

function mapOnlineQuoteProjectType(value?: string | null) {
  const normalized = normalizeComparableText(value);
  if (!normalized) return '';
  if (/renovation|extension|alteration|addition/.test(normalized)) {
    return 'Proposed Alteration and Addition to Existing Dwelling';
  }
  if (/single|home|dwelling|house/.test(normalized)) return 'Proposed Single Dwelling';
  if (/townhouse|multi|unit/.test(normalized)) return 'Proposed Multi-unit Development';
  if (/shop|retail/.test(normalized)) return 'Proposed Shop Renovation';
  if (/inspection/.test(normalized)) return 'Site Inspection';
  return toOnlineQuoteText(value);
}

function toProjectDescriptionTitleCase(value: string) {
  const lowerCaseWords = new Set(['to', 'of', 'for', 'with', 'and', 'in', 'on', 'at', 'the']);
  return value
    .split(/\s+/)
    .filter(Boolean)
    .map((word, index) => {
      if (/^[A-Z0-9-]{2,5}$/.test(word)) return word;
      const lower = word.toLowerCase();
      if (index > 0 && lowerCaseWords.has(lower)) return lower;
      return `${lower.charAt(0).toUpperCase()}${lower.slice(1)}`;
    })
    .join(' ');
}

function ensureProposedProjectDescription(value: unknown) {
  const normalized = toOnlineQuoteText(value)
    .replace(/[.;:]+$/g, '')
    .replace(/^proposed\s+/i, '')
    .trim();
  return `Proposed ${toProjectDescriptionTitleCase(normalized || 'Engineering Works')}`;
}

function buildOnlineQuoteProjectDescription(group: CompanyAiOnlineQuoteFileGroup) {
  return ensureProposedProjectDescription(
    mapOnlineQuoteProjectType(group.projectType)
    || group.helpType
    || 'Engineering Works'
  );
}

function getOnlineQuoteClientGivenAddress(group: CompanyAiOnlineQuoteFileGroup) {
  const addresses = [group.projectAddress, group.siteAddress]
    .map(toOnlineQuoteText)
    .filter(Boolean)
    .sort((left, right) => right.length - left.length);
  return addresses[0] || '';
}

function buildOnlineQuoteQuotationPrefillState(
  group: CompanyAiOnlineQuoteFileGroup,
  quotationNumber: string,
  options: {
    location?: AddressSuburbMatch | null;
    inferredProjectDescription?: string | null;
  } = {}
) {
  const projectAddress = toOnlineQuoteText(options.location?.projectAddress || getOnlineQuoteClientGivenAddress(group));
  const suburb = toOnlineQuoteText(options.location?.suburb || group.suburb);
  const postCode = toOnlineQuoteText(options.location?.postCode || group.postCode);
  const clientAddress = [projectAddress, suburb, postCode].filter(Boolean).join(', ');

  return {
    prefill: {
      quotationNumber,
      entity: 'Linfinity Engineering Pty Ltd',
      clientName: toOnlineQuoteText(group.clientName || group.uploadUserName),
      clientEmail: toOnlineQuoteText(group.clientEmail || group.uploadUserEmail || group.portalUserEmail),
      clientMobile: toOnlineQuoteText(group.phone),
      clientAddress,
      projectAddress,
      suburb,
      postCode,
      council: toOnlineQuoteText(options.location?.council || group.council),
      projectScope: mapOnlineQuoteProjectScope(group),
      projectType: mapOnlineQuoteProjectType(group.projectType),
      projectDescription: options.inferredProjectDescription
        ? ensureProposedProjectDescription(options.inferredProjectDescription)
        : buildOnlineQuoteProjectDescription(group),
      successMessage: `Loaded online quote request ${group.requestCode || group.referralLeadId} with quotation number ${quotationNumber}. Review the prefilled fields before creating the quotation row.`,
    },
  };
}

function isDuplicateProjectText(value: string, existing: string[]) {
  const normalized = normalizeComparableText(value);
  if (!normalized) return true;
  return existing.some((entry) => normalizeComparableText(entry) === normalized);
}

function buildProjectLocationLabel(project: Pick<WorkspaceProjectOption, 'siteAddress' | 'suburb' | 'postCode' | 'description'>) {
  const parts: string[] = [];
  for (const value of [project.siteAddress, formatSuburbLabel(project.suburb), project.postCode]) {
    const trimmed = String(value || '').trim();
    if (!trimmed || isDuplicateProjectText(trimmed, parts)) continue;
    parts.push(trimmed);
  }
  return parts.join(', ') || project.description || 'No address available';
}

function normalizeProjectAddressKey(value: unknown) {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

function buildProjectAddressKey(project: Pick<WorkspaceProjectOption, 'siteAddress' | 'suburb' | 'postCode'> | null | undefined) {
  if (!project) return '';
  return normalizeProjectAddressKey([project.siteAddress, project.suburb, project.postCode].filter(Boolean).join(' '));
}

function matchesBydaProject(project: WorkspaceProjectOption, record: BydaWorkspaceEnquiryRecord) {
  const recordProjectId = Number(record.project?.projectId || 0);
  if (recordProjectId > 0 && recordProjectId === project.projectId) {
    return true;
  }

  const projectKey = buildProjectAddressKey(project);
  const recordKey = buildProjectAddressKey({
    siteAddress: record.project?.siteAddress || '',
    suburb: record.project?.suburb || '',
    postCode: record.project?.postCode || '',
  });

  return Boolean(projectKey && recordKey && projectKey === recordKey);
}

function findBydaProjectRecord(project: WorkspaceProjectOption, records: BydaWorkspaceEnquiryRecord[]) {
  return (records || []).find((record) => matchesBydaProject(project, record)) || null;
}

function buildBydaExistingProjectNotice(record: BydaWorkspaceEnquiryRecord | null) {
  if (!record) return null;
  const normalizedStatus = String(record.localStatus || '').trim().toLowerCase();
  const status = formatBydaStatus(record.localStatus);
  const enquiryState = record.remoteEnquiryId || ['synced', 'response-received', 'complete', 'completed'].includes(normalizedStatus)
    ? 'existing'
    : 'pending';
  const article = enquiryState === 'existing' ? 'an' : 'a';
  const remoteText = record.remoteEnquiryId ? ` / Remote #${record.remoteEnquiryId}` : '';
  return `This address already has ${article} ${enquiryState} BYDA enquiry: #${record.bydaEnquiryId}${remoteText}, ${status}.`;
}

function normalizeProjectSearchResults(response: unknown): WorkspaceProjectOption[] {
  const rows = Array.isArray(response)
    ? response
    : (
        response
        && typeof response === 'object'
        && ('ProjectId' in response || 'Id' in response || 'ProjectCode' in response || 'ProjectName' in response)
      )
      ? [response]
      : [];

  return rows.flatMap((project: any) => {
    const projectId = Number(project?.ProjectId ?? project?.Id);
    if (!Number.isFinite(projectId) || projectId <= 0) {
      return [];
    }

    return [{
      projectId,
      projectCode: String(project?.ProjectCode || '').trim(),
      projectName: String(project?.ProjectName || project?.Title || 'Unnamed project').trim() || 'Unnamed project',
      siteAddress: String(project?.SiteAddress || project?.Address || '').trim(),
      suburb: String(project?.Suburb || '').trim(),
      postCode: String(project?.PostCode ?? project?.Postcode ?? '').trim(),
      description: String(project?.Description || '').trim(),
    }];
  });
}

function toUsagePercent(usage: CompanyAiUsage | null) {
  if (!usage || usage.monthlyTokenLimit <= 0) return 0;
  return Math.min(100, Math.round((usage.usedTokensThisPeriod / usage.monthlyTokenLimit) * 100));
}

function getConfidenceLabel(preview: CompanyAiFilePreview | null) {
  if (!preview) return 'Pending';
  if (preview.confidence >= 0.8) return 'Ready to file';
  if (preview.confidence >= 0.55) return 'Review suggested target';
  return 'Needs manual confirmation';
}

function normalizeErrorMessage(error: unknown) {
  if (error instanceof Error) return error.message;
  return 'Something went wrong.';
}

function buildConversationTail(detail: CompanyAiConversationDetail | null) {
  return (detail?.messages || [])
    .slice(-4)
    .map((message) => ({
      role: message.role,
      messageText: message.messageText,
    }));
}

function buildEffectiveConversationTail(
  detail: CompanyAiConversationDetail | null,
  dashboardFollowUp: ReturnType<typeof readCompanyAiFollowUpSeed>
) {
  const liveTail = buildConversationTail(detail);
  if (liveTail.length > 0) {
    return liveTail;
  }
  return dashboardFollowUp?.conversationTail || [];
}

type TimelineSectionEvent = {
  date: string;
  text: string;
};

function parseTimelineSectionBody(section: CompanyAiChatResult['sections'][number] | CompanyAiSearchResult['sections'][number]) {
  if (section.heading !== 'Live PMapp data') {
    return null;
  }

  const lines = String(section.body || '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  const title = lines[0] || '';
  if (!title.toLowerCase().startsWith('recent activity')) {
    return null;
  }

  const events: TimelineSectionEvent[] = [];
  const supporting: string[] = [];

  for (const line of lines.slice(1)) {
    const match = line.match(/^-?\s*(\d{4}-\d{2}-\d{2}):\s*(.+)$/);
    if (match) {
      events.push({
        date: match[1],
        text: match[2],
      });
      continue;
    }
    supporting.push(line.replace(/^- /, '').trim());
  }

  if (!events.length) {
    return null;
  }

  return {
    title,
    events,
    supporting,
  };
}

function parseStructuredSectionBody(body: string): StructuredSectionBlock[] {
  return parseStructuredAnswerBody(body);
}

function renderStructuredSectionBody(body: string) {
  const blocks = parseStructuredSectionBody(body);
  if (!blocks.length) {
    return <div className="company-ai-message-body">{body}</div>;
  }

  return (
    <div className="company-ai-rich-body">
      {blocks.map((block, index) => {
        if (block.type === 'paragraph') {
          return (
            <p key={`block-${index}`} className="company-ai-rich-paragraph">
              {block.items.join(' ')}
            </p>
          );
        }

        const ListTag = block.type === 'ordered-list' ? 'ol' : 'ul';
        return (
          <ListTag key={`block-${index}`} className="company-ai-rich-list">
            {block.items.map((item, itemIndex) => (
              <li key={`block-${index}-item-${itemIndex}`} className="company-ai-rich-item">{item}</li>
            ))}
          </ListTag>
        );
      })}
    </div>
  );
}

function renderSectionBody(section: CompanyAiChatResult['sections'][number] | CompanyAiSearchResult['sections'][number]) {
  const timeline = parseTimelineSectionBody(section);
  if (!timeline) {
    return renderStructuredSectionBody(section.body);
  }

  return (
    <div className="company-ai-timeline">
      <div className="company-ai-timeline-title">{timeline.title}</div>
      <div className="company-ai-timeline-list">
        {timeline.events.map((event, index) => (
          <div key={`${event.date}-${index}`} className="company-ai-timeline-item">
            <div className="company-ai-timeline-date">{event.date}</div>
            <div className="company-ai-timeline-copy">{event.text}</div>
          </div>
        ))}
      </div>
      {timeline.supporting.length ? (
        <div className="company-ai-message-body">
          {timeline.supporting.map((line, index) => (
            <div key={`timeline-support-${index}`}>{line}</div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default function AiWorkspacePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const consumedPromptRef = useRef<string | null>(null);
  const dashboardFollowUp = useMemo(() => readCompanyAiFollowUpSeed(location.state), [location.state]);
  const [usage, setUsage] = useState<CompanyAiUsage | null>(null);
  const [conversations, setConversations] = useState<CompanyAiConversationSummary[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<number | null>(null);
  const [conversationDetail, setConversationDetail] = useState<CompanyAiConversationDetail | null>(null);
  const [knowledgeSources, setKnowledgeSources] = useState<CompanyAiKnowledgeSource[]>([]);
  const [knowledgeFiles, setKnowledgeFiles] = useState<CompanyAiKnowledgeFile[]>([]);
  const [knowledgeFileFilter, setKnowledgeFileFilter] = useState('');
  const [selectedQuotePreviewId, setSelectedQuotePreviewId] = useState<number | null>(null);
  const [onlineQuoteFileGroups, setOnlineQuoteFileGroups] = useState<CompanyAiOnlineQuoteFileGroup[]>([]);
  const [onlineQuoteFileFilter, setOnlineQuoteFileFilter] = useState('');
  const [selectedOnlineQuoteGroupId, setSelectedOnlineQuoteGroupId] = useState<number | null>(null);
  const [onlineQuoteFileLoading, setOnlineQuoteFileLoading] = useState(false);
  const [onlineQuoteQuotationBusy, setOnlineQuoteQuotationBusy] = useState(false);
  const [onlineQuoteFileFeedback, setOnlineQuoteFileFeedback] = useState<string | null>(null);
  const [onlineQuoteFileFeedbackTone, setOnlineQuoteFileFeedbackTone] = useState<'error' | 'success'>('success');
  const [sourceScope, setSourceScope] = useState<ChatSourceScope>('company');
  const [selectedSourceIds, setSelectedSourceIds] = useState<number[]>([]);
  const [chatMessage, setChatMessage] = useState('');
  const [groundingPreview, setGroundingPreview] = useState<CompanyAiGroundingPreview | null>(null);
  const [loadingGroundingPreview, setLoadingGroundingPreview] = useState(false);
  const [chatFeedback, setChatFeedback] = useState<string | null>(null);
  const [chatFeedbackTone, setChatFeedbackTone] = useState<'error' | 'success'>('success');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [latestChatResult, setLatestChatResult] = useState<CompanyAiChatResult | null>(null);
  const [aiSearchQuery, setAiSearchQuery] = useState('');
  const [aiSearchResult, setAiSearchResult] = useState<CompanyAiQueryResult | null>(null);
  const [searchFeedback, setSearchFeedback] = useState<string | null>(null);
  const [searchFeedbackTone, setSearchFeedbackTone] = useState<'error' | 'success'>('success');
  const [runningAiSearch, setRunningAiSearch] = useState(false);
  const [activeWorkspaceTab, setActiveWorkspaceTab] = useState<CompanyAiWorkspaceTabId>(() => getCompanyAiWorkspaceTabFromSearch(location.search));
  const [autodeskOtpSending, setAutodeskOtpSending] = useState(false);
  const [autodeskOtpFeedback, setAutodeskOtpFeedback] = useState<string | null>(null);
  const [autodeskOtpFeedbackTone, setAutodeskOtpFeedbackTone] = useState<'error' | 'success'>('success');
  const [bydaEnquiries, setBydaEnquiries] = useState<BydaWorkspaceEnquiryRecord[]>([]);
  const [bydaPolicy, setBydaPolicy] = useState<BydaUsagePolicySummary | null>(null);
  const [selectedBydaEnquiryId, setSelectedBydaEnquiryId] = useState<number | null>(null);
  const [bydaDetail, setBydaDetail] = useState<BydaEnquiryDetail | null>(null);
  const [bydaDetailLoading, setBydaDetailLoading] = useState(false);
  const [bydaDetailError, setBydaDetailError] = useState<string | null>(null);
  const [bydaPreviewDocument, setBydaPreviewDocument] = useState<BydaResultDocument | null>(null);
  const [bydaFilingFeedback, setBydaFilingFeedback] = useState<string | null>(null);
  const [bydaFilingFeedbackTone, setBydaFilingFeedbackTone] = useState<'error' | 'success'>('success');
  const [bydaFiling, setBydaFiling] = useState(false);
  const [bydaYvwSewerInfo, setBydaYvwSewerInfo] = useState<BydaYvwSewerInfo | null>(null);
  const [bydaYvwSewerLoading, setBydaYvwSewerLoading] = useState(false);
  const [bydaYvwSewerError, setBydaYvwSewerError] = useState<string | null>(null);
  const [bydaProjectSearchQuery, setBydaProjectSearchQuery] = useState('');
  const [bydaProjectSearchLoading, setBydaProjectSearchLoading] = useState(false);
  const [bydaProjectSearchError, setBydaProjectSearchError] = useState<string | null>(null);
  const [bydaProjectOptions, setBydaProjectOptions] = useState<WorkspaceProjectOption[]>([]);
  const [selectedBydaProject, setSelectedBydaProject] = useState<WorkspaceProjectOption | null>(null);
  const [bydaProjectEnquiryLookup, setBydaProjectEnquiryLookup] = useState<Record<number, BydaWorkspaceEnquiryRecord[]>>({});
  const [bydaCreateDraft, setBydaCreateDraft] = useState<BydaDraftForm | null>(null);
  const [bydaCreateAutoLocateKey, setBydaCreateAutoLocateKey] = useState(0);
  const [bydaFormOptions, setBydaFormOptions] = useState<BydaFormOptions | null>(null);
  const [bydaFormOptionsLoading, setBydaFormOptionsLoading] = useState(false);
  const [bydaFormOptionsError, setBydaFormOptionsError] = useState<string | null>(null);
  const [bydaCreateSubmitting, setBydaCreateSubmitting] = useState(false);
  const [bydaCreateFeedback, setBydaCreateFeedback] = useState<string | null>(null);
  const [bydaCreateFeedbackTone, setBydaCreateFeedbackTone] = useState<'error' | 'success'>('success');

  async function loadWorkspaceInventory() {
    const nextUsage = await companyAiService.getUsage();
    const [nextConversations, nextSources, nextFiles, nextBydaEnquiries, nextOnlineQuoteFileGroups] = await Promise.all([
      companyAiService.listConversations(),
      companyAiService.listKnowledgeSources(),
      companyAiService.listKnowledgeFiles(),
      bydaService.listEnquiries().catch(() => []),
      companyAiService.listOnlineQuoteFileGroups({ limit: 200 }).catch(() => []),
    ]);
    const nextBydaPolicy = await bydaService.getPolicySummary().catch(() => null);

    return {
      nextUsage,
      nextConversations,
      nextSources,
      nextFiles,
      nextBydaEnquiries,
      nextBydaPolicy,
      nextOnlineQuoteFileGroups,
    };
  }

  useEffect(() => {
    let cancelled = false;

    async function loadWorkspace() {
      try {
        const { nextUsage, nextConversations, nextSources, nextFiles, nextBydaEnquiries, nextBydaPolicy, nextOnlineQuoteFileGroups } = await loadWorkspaceInventory();

        if (cancelled) return;

        setUsage(nextUsage);
        setConversations(nextConversations);
        setKnowledgeSources(nextSources);
        setKnowledgeFiles(nextFiles);
        setBydaEnquiries(nextBydaEnquiries);
        setBydaPolicy(nextBydaPolicy);
        setOnlineQuoteFileGroups(nextOnlineQuoteFileGroups);
        setSelectedOnlineQuoteGroupId((current) => (
          current && nextOnlineQuoteFileGroups.some((group) => group.referralLeadId === current)
            ? current
            : nextOnlineQuoteFileGroups[0]?.referralLeadId || null
        ));

        const firstConversationId = activeConversationId || (!dashboardFollowUp ? nextConversations[0]?.aiConversationId : null) || null;
        setActiveConversationId(firstConversationId);

        if (firstConversationId) {
          const detail = await companyAiService.getConversation(firstConversationId);
          if (!cancelled) {
            setConversationDetail(detail);
          }
        } else if (!cancelled) {
          setConversationDetail(null);
        }
      } catch (error) {
        if (!cancelled) {
          setChatFeedback(normalizeErrorMessage(error));
          setChatFeedbackTone('error');
        }
      }
    }

    void loadWorkspace();
    return () => {
      cancelled = true;
    };
  }, [activeConversationId, dashboardFollowUp]);

  useEffect(() => {
    const nextTab = getCompanyAiWorkspaceTabFromSearch(location.search);
    setActiveWorkspaceTab((current) => (current === nextTab ? current : nextTab));
  }, [location.search]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const prompt = params.get('prompt')?.trim() || dashboardFollowUp?.prompt || '';
    if (!prompt) {
      return;
    }

    if (consumedPromptRef.current === prompt) {
      return;
    }

    consumedPromptRef.current = prompt;
    setChatMessage(prompt);
    setAiSearchQuery(prompt);
    setChatFeedback(dashboardFollowUp?.conversationTail?.length ? 'Dashboard follow-up context loaded for workspace continuation.' : 'Draft question loaded from the dashboard.');
    setChatFeedbackTone('success');
  }, [location.search, dashboardFollowUp?.prompt, dashboardFollowUp?.conversationTail?.length]);

  useEffect(() => {
    const trimmedMessage = chatMessage.trim();
    if (!trimmedMessage) {
      setGroundingPreview(null);
      setLoadingGroundingPreview(false);
      return;
    }

    let cancelled = false;
    const timeoutId = window.setTimeout(() => {
      setLoadingGroundingPreview(true);
      void companyAiService.previewGrounding({
        message: trimmedMessage,
        sourceScope,
        selectedSourceIds: sourceScope === 'selected' ? selectedSourceIds : [],
        surface: 'workspace',
        conversationTail: buildEffectiveConversationTail(conversationDetail, dashboardFollowUp),
      }).then((preview) => {
        if (cancelled) return;
        setGroundingPreview(preview);
      }).catch(() => {
        if (cancelled) return;
        setGroundingPreview(null);
      }).finally(() => {
        if (cancelled) return;
        setLoadingGroundingPreview(false);
      });
    }, 260);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [chatMessage, conversationDetail, selectedSourceIds, sourceScope, dashboardFollowUp?.prompt, dashboardFollowUp?.conversationTail]);

  async function refreshWorkspace(nextConversationId?: number | null) {
    const { nextUsage, nextConversations, nextSources, nextFiles, nextBydaEnquiries, nextBydaPolicy, nextOnlineQuoteFileGroups } = await loadWorkspaceInventory();

    setUsage(nextUsage);
    setConversations(nextConversations);
    setKnowledgeSources(nextSources);
    setKnowledgeFiles(nextFiles);
    setBydaEnquiries(nextBydaEnquiries);
    setBydaPolicy(nextBydaPolicy);
    setOnlineQuoteFileGroups(nextOnlineQuoteFileGroups);
    setSelectedOnlineQuoteGroupId((current) => (
      current && nextOnlineQuoteFileGroups.some((group) => group.referralLeadId === current)
        ? current
        : nextOnlineQuoteFileGroups[0]?.referralLeadId || null
    ));

    const resolvedConversationId = nextConversationId || activeConversationId || (!dashboardFollowUp ? nextConversations[0]?.aiConversationId : null) || null;
    setActiveConversationId(resolvedConversationId);

    if (resolvedConversationId) {
      const detail = await companyAiService.getConversation(resolvedConversationId);
      setConversationDetail(detail);
    } else {
      setConversationDetail(null);
    }
  }

  useEffect(() => {
    if (selectedBydaEnquiryId && bydaEnquiries.some((item) => item.bydaEnquiryId === selectedBydaEnquiryId)) {
      return;
    }
    setSelectedBydaEnquiryId(bydaEnquiries[0]?.bydaEnquiryId || null);
  }, [bydaEnquiries, selectedBydaEnquiryId]);

  useEffect(() => {
    const query = bydaProjectSearchQuery.trim();
    const selectedProjectQuery = selectedBydaProject ? buildProjectSearchValue(selectedBydaProject) : '';

    if (!query || query === selectedProjectQuery || query.length < 2) {
      setBydaProjectSearchLoading(false);
      setBydaProjectOptions([]);
      setBydaProjectSearchError(null);
      return;
    }

    let active = true;
    const timeoutId = window.setTimeout(() => {
      setBydaProjectSearchLoading(true);
      setBydaProjectSearchError(null);
      void azureSqlService.searchProjects(query, 8)
        .then((response: unknown) => {
          if (!active) return;
          setBydaProjectOptions(normalizeProjectSearchResults(response));
        })
        .catch((error: unknown) => {
          if (!active) return;
          setBydaProjectOptions([]);
          setBydaProjectSearchError(normalizeErrorMessage(error));
        })
        .finally(() => {
          if (!active) return;
          setBydaProjectSearchLoading(false);
        });
    }, 220);

    return () => {
      active = false;
      window.clearTimeout(timeoutId);
    };
  }, [bydaProjectSearchQuery, selectedBydaProject]);

  const bydaProjectIdsToCheck = useMemo(() => {
    const ids = new Set<number>();
    for (const project of bydaProjectOptions) {
      if (project.projectId > 0 && !(project.projectId in bydaProjectEnquiryLookup)) {
        ids.add(project.projectId);
      }
    }
    if (selectedBydaProject?.projectId && !(selectedBydaProject.projectId in bydaProjectEnquiryLookup)) {
      ids.add(selectedBydaProject.projectId);
    }
    return Array.from(ids);
  }, [bydaProjectOptions, selectedBydaProject, bydaProjectEnquiryLookup]);

  useEffect(() => {
    if (!bydaProjectIdsToCheck.length) return;

    let cancelled = false;
    void Promise.all(
      bydaProjectIdsToCheck.map(async (projectId) => {
        const records = await bydaService.listEnquiries({ projectId, limit: 3 }).catch(() => []);
        return [projectId, records] as const;
      })
    ).then((entries) => {
      if (cancelled) return;
      setBydaProjectEnquiryLookup((current) => {
        const next = { ...current };
        for (const [projectId, records] of entries) {
          next[projectId] = records;
        }
        return next;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [bydaProjectIdsToCheck]);

  function handleSelectBydaProject(project: WorkspaceProjectOption) {
    setSelectedBydaProject(project);
    setBydaProjectSearchQuery(buildProjectSearchValue(project));
    setBydaProjectOptions([]);
    setBydaProjectSearchError(null);
    setBydaCreateFeedback(null);
    setBydaFormOptions(null);
    setBydaFormOptionsError(null);
    setBydaCreateDraft(createBydaDraftFromProject({
      projectId: project.projectId,
      projectCode: project.projectCode,
      siteAddress: project.siteAddress,
      suburb: project.suburb,
      postCode: project.postCode,
    }));
    setBydaCreateAutoLocateKey((current) => current + 1);
  }

  function resetBydaCreateSelection() {
    setSelectedBydaProject(null);
    setBydaProjectSearchQuery('');
    setBydaProjectSearchLoading(false);
    setBydaProjectSearchError(null);
    setBydaProjectOptions([]);
    setBydaCreateDraft(null);
    setBydaCreateAutoLocateKey(0);
    setBydaCreateFeedback(null);
    setBydaFormOptions(null);
    setBydaFormOptionsError(null);
  }

  const setBydaCreateField = <K extends keyof BydaDraftForm>(field: K, value: BydaDraftForm[K]) => {
    setBydaCreateDraft((current) => (current ? { ...current, [field]: value } : current));
  };

  const setBydaCreatePoints = (points: BydaPointDraft[]) => {
    setBydaCreateDraft((current) => (current ? { ...current, points } : current));
  };

  const updateBydaCreateCodeSelection = (field: 'activityTypes', code: string, enabled: boolean) => {
    setBydaCreateDraft((current) => {
      if (!current) return current;
      return {
        ...current,
        [field]: toggleBydaCode(current[field], code, enabled),
      };
    });
  };

  const updateBydaCreateWorkplaceSelection = (code: string, enabled: boolean) => {
    setBydaCreateDraft((current) => {
      if (!current) return current;
      return {
        ...current,
        workplaceSelections: toggleBydaCode(current.workplaceSelections, code, enabled),
      };
    });
  };

  const bydaCreateShape = bydaCreateDraft ? tryBuildPolygonFromDraftPoints(bydaCreateDraft.points) : null;
  const bydaCreateShapeKey = JSON.stringify(bydaCreateShape?.coordinates || null);

  useEffect(() => {
    if (!selectedBydaProject?.projectId || !bydaCreateDraft) {
      setBydaFormOptions(null);
      setBydaFormOptionsError(null);
      setBydaFormOptionsLoading(false);
      return;
    }

    let cancelled = false;
    setBydaFormOptionsLoading(true);
    setBydaFormOptionsError(null);

    void bydaService.getFormOptions({
      projectId: selectedBydaProject.projectId,
      isPlanningJob: bydaCreateDraft.isPlanningJob,
      shape: bydaCreateShape,
    })
      .then((options) => {
        if (cancelled) return;
        setBydaFormOptions(options);
        setBydaCreateDraft((current) => {
          if (!current) return current;
          return {
            ...current,
            activityTypes: syncBydaCodes(current.activityTypes, options.activityTypes, {
              fallback: getDefaultBydaDesignActivityCodes(options.activityTypes),
              allowEmpty: false,
            }),
            authorityId: options.authorityOptions.some((option) => String(option.id) === current.authorityId)
              ? current.authorityId
              : '',
          };
        });
      })
      .catch((error) => {
        if (cancelled) return;
        setBydaFormOptions(null);
        setBydaFormOptionsError(normalizeErrorMessage(error));
      })
      .finally(() => {
        if (cancelled) return;
        setBydaFormOptionsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedBydaProject?.projectId, bydaCreateDraft?.isPlanningJob, bydaCreateShapeKey]);

  async function handleCreateBydaEnquiry() {
    if (!selectedBydaProject || !bydaCreateDraft || bydaCreateSubmitting) {
      return;
    }

    setBydaCreateSubmitting(true);
    setBydaCreateFeedback(null);

    try {
      if (bydaFormOptionsLoading) {
        throw new Error('BYDA options are still loading.');
      }
      if (!bydaFormOptions) {
        throw new Error(bydaFormOptionsError || 'BYDA options are unavailable right now.');
      }

      const activityTypes = bydaCreateDraft.activityTypes;
      const { locationTypes, locationsInRoad } = mapWorkplaceSelectionsToPayload(bydaCreateDraft.workplaceSelections);
      if (activityTypes.length === 0) {
        throw new Error('Choose at least one BYDA activity type.');
      }
      if (bydaCreateDraft.workplaceSelections.length === 0) {
        throw new Error('Choose at least one workplace location.');
      }

      const payload: CreateBydaEnquiryInput = {
        projectId: selectedBydaProject.projectId,
        userReference: normalizeDraftField(bydaCreateDraft.userReference),
        digStartAt: bydaCreateDraft.digStartAt,
        digEndAt: bydaCreateDraft.digEndAt,
        shape: buildBydaDraftPolygon(bydaCreateDraft.points),
        isPlanningJob: bydaCreateDraft.isPlanningJob,
        activityTypes,
        authorityId: bydaCreateDraft.authorityId ? Number(bydaCreateDraft.authorityId) : undefined,
        notes: normalizeDraftField(bydaCreateDraft.notes),
        locationTypes,
        locationsInRoad,
        isSandboxTest: bydaCreateDraft.isSandboxTest,
        userTimezone: normalizeDraftField(bydaCreateDraft.userTimezone),
        address: {
          line1: normalizeDraftField(bydaCreateDraft.addressLine1),
          locality: normalizeDraftField(bydaCreateDraft.locality),
          postcode: normalizeDraftField(bydaCreateDraft.postcode),
          state: normalizeDraftField(bydaCreateDraft.state),
          country: normalizeDraftField(bydaCreateDraft.country),
        },
      };

      const preview = await bydaService.previewEnquiry(payload);
      setBydaPolicy(preview.policy);

      if (!preview.canSubmit) {
        throw new Error(buildBydaBlockedMessage(preview));
      }

      const confirmed = window.confirm(buildBydaConfirmationMessage(preview));
      if (!confirmed) {
        setBydaCreateFeedbackTone('success');
        setBydaCreateFeedback('BYDA enquiry cancelled before any API call was sent.');
        return;
      }

      const record = await bydaService.createEnquiry({
        ...payload,
        confirmSubmission: true,
      });
      const refreshedEnquiries = await bydaService.listEnquiries().catch(() => []);
      const refreshedPolicy = await bydaService.getPolicySummary().catch(() => preview.policy);
      setBydaEnquiries((current) => (
        refreshedEnquiries.length
          ? refreshedEnquiries
          : (record ? [record, ...current.filter((item) => item.bydaEnquiryId !== record.bydaEnquiryId)] : current)
      ));
      setBydaPolicy(refreshedPolicy);
      setSelectedBydaEnquiryId(record?.bydaEnquiryId || null);
      setBydaCreateFeedbackTone('success');
      setBydaCreateFeedback(
        record?.remoteEnquiryId
          ? `BYDA enquiry created. Remote enquiry #${record.remoteEnquiryId} is now linked to ${selectedBydaProject.projectCode || selectedBydaProject.projectName}.`
          : 'BYDA enquiry created and added to the workspace.'
      );
      setBydaCreateDraft(createBydaDraftFromProject({
        projectId: selectedBydaProject.projectId,
        projectCode: selectedBydaProject.projectCode,
        siteAddress: selectedBydaProject.siteAddress,
        suburb: selectedBydaProject.suburb,
        postCode: selectedBydaProject.postCode,
      }));
      setBydaCreateAutoLocateKey((current) => current + 1);
    } catch (error) {
      void bydaService.getPolicySummary().then((policy) => setBydaPolicy(policy)).catch(() => undefined);
      setBydaCreateFeedbackTone('error');
      setBydaCreateFeedback(normalizeErrorMessage(error));
    } finally {
      setBydaCreateSubmitting(false);
    }
  }

  async function loadBydaDetail(bydaEnquiryId: number, options?: { refreshRemote?: boolean }) {
    setBydaDetailLoading(true);
    setBydaDetailError(null);

    try {
      const detail = await bydaService.getEnquiryDetail(bydaEnquiryId, {
        refreshRemote: options?.refreshRemote ?? true,
        includeResults: true,
      });
      setBydaDetail(detail);
      return detail;
    } catch (error) {
      setBydaDetail(null);
      setBydaDetailError(normalizeErrorMessage(error));
      return null;
    } finally {
      setBydaDetailLoading(false);
    }
  }

  useEffect(() => {
    if (!selectedBydaEnquiryId) {
      setBydaDetail(null);
      setBydaDetailError(null);
      setBydaYvwSewerInfo(null);
      setBydaYvwSewerError(null);
      return;
    }

    let cancelled = false;
    setBydaFilingFeedback(null);
    setBydaYvwSewerInfo(null);
    setBydaYvwSewerError(null);

    void loadBydaDetail(selectedBydaEnquiryId, {
      refreshRemote: false,
    }).then((detail) => {
      if (cancelled) return;
      if (detail) {
        setBydaDetail(detail);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [selectedBydaEnquiryId]);

  async function handleBydaFileResults() {
    if (!selectedBydaEnquiryId || bydaFiling) {
      return;
    }

    setBydaFiling(true);
    setBydaFilingFeedback(null);
    setBydaDetailError(null);
    try {
      const detail = await bydaService.fileEnquiryResults(selectedBydaEnquiryId);
      setBydaDetail(detail);
      setBydaFilingFeedbackTone(detail.failedCount && detail.failedCount > 0 ? 'error' : 'success');
      setBydaFilingFeedback(
        detail.failedCount && detail.failedCount > 0
          ? `Filed ${detail.uploadedCount || 0} BYDA result file${detail.uploadedCount === 1 ? '' : 's'}, with ${detail.failedCount} item${detail.failedCount === 1 ? '' : 's'} still needing attention.`
          : `Filed ${detail.uploadedCount || 0} BYDA result file${detail.uploadedCount === 1 ? '' : 's'} to the project folder.`
      );
      setBydaEnquiries((current) => current.map((item) => (
        item.bydaEnquiryId === selectedBydaEnquiryId && detail.record
          ? detail.record
          : item
      )));
    } catch (error) {
      setBydaFilingFeedbackTone('error');
      setBydaFilingFeedback(normalizeErrorMessage(error));
    } finally {
      setBydaFiling(false);
    }
  }

  async function handleFetchBydaYvwSewerInfo() {
    if (!selectedBydaEnquiryId || bydaYvwSewerLoading) {
      return;
    }

    setBydaYvwSewerLoading(true);
    setBydaYvwSewerError(null);
    try {
      const sewerInfo = await bydaService.getYvwSewerInfo(selectedBydaEnquiryId);
      setBydaYvwSewerInfo(sewerInfo);
    } catch (error) {
      setBydaYvwSewerInfo(null);
      setBydaYvwSewerError(normalizeErrorMessage(error));
    } finally {
      setBydaYvwSewerLoading(false);
    }
  }

  async function handleRefreshOnlineQuoteFiles() {
    setOnlineQuoteFileLoading(true);
    setOnlineQuoteFileFeedback(null);

    try {
      const groups = await companyAiService.listOnlineQuoteFileGroups({
        query: onlineQuoteFileFilter,
        limit: 300,
      });
      setOnlineQuoteFileGroups(groups);
      setSelectedOnlineQuoteGroupId((current) => (
        current && groups.some((group) => group.referralLeadId === current)
          ? current
          : groups[0]?.referralLeadId || null
      ));
      const fileCount = groups.reduce((sum, group) => sum + group.files.length, 0);
      setOnlineQuoteFileFeedback(`Loaded ${groups.length} quote request group${groups.length === 1 ? '' : 's'} with ${fileCount} file${fileCount === 1 ? '' : 's'}.`);
      setOnlineQuoteFileFeedbackTone('success');
    } catch (error) {
      setOnlineQuoteFileFeedback(normalizeErrorMessage(error));
      setOnlineQuoteFileFeedbackTone('error');
    } finally {
      setOnlineQuoteFileLoading(false);
    }
  }

  async function handleCreateQuotationFromOnlineQuoteGroup() {
    if (!selectedOnlineQuoteGroup || selectedOnlineQuoteGroupQuarantined) {
      return;
    }

    setOnlineQuoteQuotationBusy(true);
    setOnlineQuoteFileFeedback(null);

    try {
      const clientGivenAddress = getOnlineQuoteClientGivenAddress(selectedOnlineQuoteGroup);
      const [status, matchedLocation, inferredDescription] = await Promise.all([
        azureSqlService.getWorkflowQuotationNumberStatus(''),
        matchAddressToSuburbList({
          address: clientGivenAddress,
          suburb: selectedOnlineQuoteGroup.suburb,
          postCode: selectedOnlineQuoteGroup.postCode,
          council: selectedOnlineQuoteGroup.council,
        }).catch(() => null),
        companyAiService.inferQuotationProjectDescription({
          userDescription: selectedOnlineQuoteGroup.notes,
          helpType: selectedOnlineQuoteGroup.helpType,
          projectType: selectedOnlineQuoteGroup.projectType,
        }).catch(() => null),
      ]);
      const quotationNumber = toOnlineQuoteText(
        status?.recommendedQuotationNumber
        || status?.nextAvailableQuotationNumber
        || status?.quotationNumber
      );

      if (!quotationNumber) {
        throw new Error('Could not resolve the next available quotation number.');
      }

      navigate('/quotations/workflow', {
        state: buildOnlineQuoteQuotationPrefillState(selectedOnlineQuoteGroup, quotationNumber, {
          location: matchedLocation,
          inferredProjectDescription: inferredDescription?.description,
        }),
      });
    } catch (error) {
      setOnlineQuoteFileFeedback(normalizeErrorMessage(error));
      setOnlineQuoteFileFeedbackTone('error');
    } finally {
      setOnlineQuoteQuotationBusy(false);
    }
  }

  async function handleSelectConversation(conversationId: number) {
    setActiveConversationId(conversationId);
    setChatFeedback(null);
    setLatestChatResult(null);
    const detail = await companyAiService.getConversation(conversationId);
    setConversationDetail(detail);
  }

  async function handleSendMessage(event: React.FormEvent) {
    event.preventDefault();
    const message = chatMessage.trim();
    if (!message) {
      setChatFeedback('Enter a question before sending it to Company AI.');
      setChatFeedbackTone('error');
      return;
    }

    setSendingMessage(true);
    setChatFeedback(null);

    try {
      const result = await companyAiService.chat({
        conversationId: activeConversationId,
        message,
        sourceScope,
        surface: 'workspace',
        selectedSourceIds: sourceScope === 'selected' ? selectedSourceIds : [],
        conversationTail: buildEffectiveConversationTail(conversationDetail, dashboardFollowUp),
      });

      setChatMessage('');
      setLatestChatResult(result);
      setUsage(result.quota);
      await refreshWorkspace(result.aiConversationId);
      const groundingParts = [
        `${result.citations.length} cited source${result.citations.length === 1 ? '' : 's'}`,
        `${result.pmContext.length} live PMapp match${result.pmContext.length === 1 ? '' : 'es'}`,
      ];
      setChatFeedback(`Answer generated with ${groundingParts.join(' and ')}.`);
      setChatFeedbackTone('success');
    } catch (error) {
      setChatFeedback(normalizeErrorMessage(error));
      setChatFeedbackTone('error');
    } finally {
      setSendingMessage(false);
    }
  }

  async function handleRunAiSearch(event: React.FormEvent) {
    event.preventDefault();
    const query = aiSearchQuery.trim();
    if (!query) {
      setSearchFeedback('Enter a question before running PM Query.');
      setSearchFeedbackTone('error');
      return;
    }

    setRunningAiSearch(true);
    setSearchFeedback(null);
    try {
      const result = aiSearchResult?.actionType === 'clarify' && aiSearchResult.sessionId
        ? await companyAiService.queryAgent({
            sessionId: aiSearchResult.sessionId,
            message: query,
          })
        : await companyAiService.queryAgent({
            message: query,
          });
      setAiSearchResult(result);
      setSearchFeedback(
        result.actionType === 'clarify'
          ? 'PM Query needs one more detail before continuing.'
          : result.agentSteps?.length
            ? `PM Query ran ${result.agentSteps.length} grounded step${result.agentSteps.length === 1 ? '' : 's'} and returned ${result.candidates.length} candidate${result.candidates.length === 1 ? '' : 's'}.`
            : `PM Query returned ${result.candidates.length} candidate${result.candidates.length === 1 ? '' : 's'}.`
      );
      setSearchFeedbackTone('success');
      setAiSearchQuery('');
    } catch (error) {
      setSearchFeedback(normalizeErrorMessage(error));
      setSearchFeedbackTone('error');
    } finally {
      setRunningAiSearch(false);
    }
  }

  async function handleSendAutodeskOtp() {
    setAutodeskOtpSending(true);
    setAutodeskOtpFeedback(null);

    try {
      const result = await azureSqlService.sendAutodeskOtpForwarderTrigger();
      if (result?.sent) {
        const maskedCode = String(result.maskedCode || '').trim();
        const codeLabel = maskedCode ? ` ${maskedCode}` : '';
        const subjectLabel = String(result.originalSubject || '').trim();
        setAutodeskOtpFeedback(`Autodesk OTP${codeLabel} emailed to ${result.recipientEmail}.${subjectLabel ? ` Source: ${subjectLabel}.` : ''}`);
        setAutodeskOtpFeedbackTone('success');
        return;
      }

      const reason = String(result?.reason || '').trim() || 'No recent Autodesk OTP email was found.';
      setAutodeskOtpFeedback(reason);
      setAutodeskOtpFeedbackTone('error');
    } catch (error) {
      setAutodeskOtpFeedback(normalizeErrorMessage(error));
      setAutodeskOtpFeedbackTone('error');
    } finally {
      setAutodeskOtpSending(false);
    }
  }

  function toggleSelectedSource(sourceId: number) {
    setSelectedSourceIds((current) => (
      current.includes(sourceId)
        ? current.filter((value) => value !== sourceId)
        : [...current, sourceId]
    ));
  }

  const roleLabel = getPrimaryRoleLabel(user?.userRoles, user?.userDetails);
  const usagePercent = toUsagePercent(usage);
  const readySources = knowledgeSources.filter((source) => source.status === 'ready' && source.isEnabled);
  const pendingSources = knowledgeSources.filter((source) => source.status === 'processing' || source.status === 'queued');
  const erroredSources = knowledgeSources.filter((source) => source.status === 'error');
  const bydaSubmittedCount = bydaEnquiries.filter((item) => ['submitted', 'synced'].includes(String(item.localStatus || '').toLowerCase())).length;
  const bydaFailedCount = bydaEnquiries.filter((item) => String(item.localStatus || '').toLowerCase() === 'failed').length;
  const bydaRemoteCount = bydaEnquiries.filter((item) => Number(item.remoteEnquiryId || 0) > 0).length;
  const bydaSelectedRecord = bydaDetail?.record || bydaEnquiries.find((item) => item.bydaEnquiryId === selectedBydaEnquiryId) || null;
  const bydaResultDocuments = bydaDetail?.resultDocuments || [];
  const bydaCreateAddressQuery = [
    bydaCreateDraft?.addressLine1 || '',
    bydaCreateDraft?.locality || '',
    bydaCreateDraft?.postcode || '',
    bydaCreateDraft?.state || '',
  ].filter(Boolean).join(', ');
  const selectedBydaProjectRecords = selectedBydaProject
    ? (bydaProjectEnquiryLookup[selectedBydaProject.projectId] || bydaEnquiries)
    : [];
  const selectedBydaProjectNotice = selectedBydaProject
    ? buildBydaExistingProjectNotice(findBydaProjectRecord(selectedBydaProject, selectedBydaProjectRecords))
    : null;
  const filteredKnowledgeFiles = knowledgeFiles.filter((file) => {
    const filter = knowledgeFileFilter.trim().toLowerCase();
    if (!filter) return true;
    const haystack = [
      file.sourceTitle,
      file.fileName,
      file.filePath,
    ].join(' ').toLowerCase();
    return haystack.includes(filter);
  });
  const filteredOnlineQuoteFileGroups = onlineQuoteFileGroups.filter((group) => {
    const filter = onlineQuoteFileFilter.trim().toLowerCase();
    if (!filter) return true;
    const haystack = [
      group.requestCode,
      group.referralLeadId,
      group.uploadUserName,
      group.uploadUserEmail,
      group.siteAddress,
      group.clientName,
      group.clientEmail,
      group.projectAddress,
      group.suburb,
      group.council,
      group.portalUserEmail,
      group.phone,
      group.projectType,
      group.customerType,
      group.helpType,
      group.notes,
      group.spamScore,
      ...(group.spamReasons || []),
      ...group.files.flatMap((file) => [file.fileName, file.contentType]),
    ].join(' ').toLowerCase();
    return haystack.includes(filter);
  });
  const selectedOnlineQuoteGroup = filteredOnlineQuoteFileGroups.find((group) => group.referralLeadId === selectedOnlineQuoteGroupId)
    || filteredOnlineQuoteFileGroups[0]
    || null;
  const selectedOnlineQuoteGroupQuarantined = selectedOnlineQuoteGroup ? isQuarantinedOnlineQuoteGroup(selectedOnlineQuoteGroup) : false;
  const selectedOnlineQuotePreviewFile = selectedOnlineQuoteGroup?.files.find((file) => file.referralLeadFileId === selectedQuotePreviewId)
    || selectedOnlineQuoteGroup?.files[0]
    || null;

  const onlineQuoteFileCount = onlineQuoteFileGroups.reduce((sum, group) => sum + group.files.length, 0);
  const filteredOnlineQuoteFileCount = filteredOnlineQuoteFileGroups.reduce((sum, group) => sum + group.files.length, 0);
  const canManageKnowledgeSources = hasAdminEquivalentAccess(user?.userRoles, user?.userDetails);
  const canManageIfcModels = hasRequiredRoleAccess(user?.userRoles, user?.userDetails, ['pm']);
  const canManageProjectFiles = canManageIfcModels;
  const activeConversationTitle = conversationDetail?.conversation?.title || 'New Company AI conversation';
  const activeConversationSubtitle = conversationDetail
    ? `Last updated ${formatDateTime(conversationDetail.conversation.lastMessageAt || conversationDetail.conversation.createdDate)}`
    : 'Ask about company references, project data, invoices, quotations, or filing workflows.';
  const latestAssistantMessageId = conversationDetail?.messages?.length
    ? conversationDetail.messages[conversationDetail.messages.length - 1]?.aiMessageId
    : null;

  const askAiTab = (
    <PageSection width="full" padding="default">
      <div className="company-ai-layout">
        <div className="company-ai-primary">
          <Card
            className="company-ai-chat-workspace"
            header={(
              <CardHeader
                title={activeConversationTitle}
                subtitle={activeConversationSubtitle}
                actions={(
                  <div className="company-ai-header-actions">
                    <StatusBadge status={sourceScope}>{sourceScope === 'selected' ? 'Selected sources' : `${sourceScope} scope`}</StatusBadge>
                    <StatusBadge status={usage?.isEnabled ? 'active' : 'inactive'}>
                      {usage?.isEnabled ? 'Ready' : 'Disabled'}
                    </StatusBadge>
                  </div>
                )}
              />
            )}
          >
            <div className="company-ai-chat-card">
              <div className="company-ai-chat-toolbar">
                <div className="company-ai-source-strip">
                  <span className="company-ai-source-pill">{readySources.length} indexed source{readySources.length === 1 ? '' : 's'} ready</span>
                  <span className="company-ai-source-pill">{conversations.length} your thread{conversations.length === 1 ? '' : 's'}</span>
                  <span className="company-ai-source-pill">Read-only PM data grounding</span>
                </div>
                <div className="company-ai-muted">
                  {usage ? `${usage.remainingTokensThisPeriod.toLocaleString()} tokens remaining this month.` : 'Loading quota...'}
                </div>
              </div>

              {conversationDetail?.messages?.length ? (
                <div className="company-ai-message-list">
                  {conversationDetail.messages.map((message) => (
                    (() => {
                      const latestStructuredSections = message.role === 'assistant'
                        && latestChatResult
                        && latestChatResult.aiConversationId === activeConversationId
                        && message.aiMessageId === latestAssistantMessageId
                        && latestChatResult.sections.length
                        ? latestChatResult.sections
                        : null;
                      const explanationCaption = latestStructuredSections?.find((section) => section.heading === 'What this means')?.body || null;
                      const visibleSections = (latestStructuredSections || []).filter((section) => section.heading !== 'What this means');

                      return (
                        <article
                          key={message.aiMessageId}
                          className={`company-ai-message${message.role === 'assistant' ? ' company-ai-message--assistant' : ' company-ai-message--user'}`}
                        >
                          <div className="company-ai-message-meta">
                            <strong>{message.role === 'assistant' ? 'Company AI' : 'You'}</strong>
                            <span>{formatDateTime(message.createdDate)}</span>
                            {explanationCaption ? (
                              <span className="company-ai-message-caption">{explanationCaption}</span>
                            ) : null}
                          </div>
                          {visibleSections.length ? (
                            <div className="company-ai-stacked">
                              {visibleSections.map((section, index) => (
                                <div key={`${message.aiMessageId}-section-${index}`} className="company-ai-result-box">
                                  <div className="company-ai-section-title" style={{ marginTop: 0 }}>{section.heading}</div>
                                  {renderSectionBody(section)}
                                </div>
                              ))}
                            </div>
                          ) : (
                            renderStructuredSectionBody(message.messageText)
                          )}

                          {message.citations.length ? (
                            <>
                              <div className="company-ai-section-title">Citations</div>
                              <div className="company-ai-citation-list">
                                {message.citations.map((citation, index) => (
                                  <div key={`${message.aiMessageId}-${citation.sourceId}-${index}`} className="company-ai-citation">
                                    <div className="company-ai-citation-title">{citation.title}</div>
                                    <div className="company-ai-citation-copy">
                                      {citation.excerpt}
                                      {citation.filePath ? ` (${citation.filePath})` : ''}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </>
                          ) : null}

                          {message.pmContext.length ? (
                            <>
                              <div className="company-ai-section-title">Live PMapp Data</div>
                              <div className="company-ai-action-list">
                                {message.pmContext.map((item, index) => (
                                  <div key={`${message.aiMessageId}-${item.domain}-${index}`} className="company-ai-action">
                                    <div className="company-ai-action-title">{item.title}</div>
                                    <div className="company-ai-action-copy">{item.summary}</div>
                                  </div>
                                ))}
                              </div>
                            </>
                          ) : null}
                        </article>
                      );
                    })()
                  ))}
                </div>
              ) : (
                <div className="company-ai-empty-chat">
                  <EmptyState
                    icon="🧠"
                    title="Ask a question to start"
                    description="Answers can cite indexed company references and include read-only PMapp record summaries when they match."
                  />
                </div>
              )}

              <form onSubmit={handleSendMessage} className="company-ai-composer">
                <Textarea
                  label="Question"
                  value={chatMessage}
                  onChange={(event) => setChatMessage(event.target.value)}
                  placeholder="Example: what is the current invoice status for Project 26016, and what company file explains the filing workflow?"
                  rows={2}
                  density="compact"
                  fullWidth
                />

                <div className="company-ai-form-actions">
                  <Button type="submit" loading={sendingMessage}>
                    Send To Company AI
                  </Button>
                  <span className="company-ai-muted">
                    {sourceScope === 'selected'
                      ? `${selectedSourceIds.length} source${selectedSourceIds.length === 1 ? '' : 's'} selected for grounding.`
                      : `Using ${sourceScope} knowledge scope.`}
                  </span>
                </div>

                {chatFeedback ? (
                  <div className={`company-ai-feedback company-ai-feedback--${chatFeedbackTone}`}>
                    {chatFeedback}
                  </div>
                ) : null}
              </form>
            </div>
          </Card>

          {canManageKnowledgeSources ? (
            <Card
              header={(
                <CardHeader
                  title="Autodesk OTP"
                  subtitle="Manual-only helper for WD to fetch the latest Autodesk one-time passcode right after requesting it."
                  actions={<StatusBadge status="10">Scans latest 10 emails</StatusBadge>}
                />
              )}
            >
              <div className="company-ai-stacked">
                <div className="company-ai-result-box company-ai-result-box--compact">
                  <div className="company-ai-section-title company-ai-section-title--flush">How it works</div>
                  <div className="company-ai-message-body">
                    Click once after Autodesk sends the code. PMapp checks the newest 10 emails in `YL`, finds Autodesk OTP messages, and only sends the latest matching code to `WD`.
                  </div>
                </div>

                <div className="company-ai-form-actions">
                  <Button loading={autodeskOtpSending} onClick={() => void handleSendAutodeskOtp()}>
                    {autodeskOtpSending ? 'Fetching Autodesk OTP...' : 'Fetch Autodesk OTP'}
                  </Button>
                </div>

                {autodeskOtpFeedback ? (
                  <div className={`company-ai-feedback company-ai-feedback--${autodeskOtpFeedbackTone}`}>
                    {autodeskOtpFeedback}
                  </div>
                ) : null}
              </div>
            </Card>
          ) : null}
        </div>

        <div className="company-ai-sidebar">
          <Card
            header={(
              <CardHeader
                title="Usage And Coverage"
                subtitle="Track your monthly AI quota and what the assistant can currently ground."
                actions={<StatusBadge status={usage?.isEnabled ? 'active' : 'inactive'}>{usage?.isEnabled ? 'Enabled' : 'Disabled'}</StatusBadge>}
              />
            )}
          >
            <div className="company-ai-stacked">
              <div className="company-ai-kpi-card">
                <div className="company-ai-kpi-value">{usage ? usage.remainingTokensThisPeriod.toLocaleString() : '...'}</div>
                <div className="company-ai-kpi-label">tokens remaining this month</div>
                <div className="company-ai-meter">
                  <div className="company-ai-meter-fill" style={{ width: `${usagePercent}%` }} />
                </div>
              </div>
              <div className="company-ai-inline-metrics">
                <div className="company-ai-inline-metric">
                  <strong>{usage?.usedTokensThisPeriod.toLocaleString() || 0}</strong>
                  <span>Used this month</span>
                </div>
                <div className="company-ai-inline-metric">
                  <strong>{readySources.length}</strong>
                  <span>Ready sources</span>
                </div>
              </div>
            </div>
          </Card>

          <FormSection
            title="Knowledge Scope"
            description="Choose whether the assistant searches company-wide sources or a hand-picked source set."
            icon="🗂️"
            accentColor="#2251b4"
          >
            <div className="company-ai-form-grid">
              <Select
                label="Source scope"
                value={sourceScope}
                onChange={(event) => setSourceScope(event.target.value as ChatSourceScope)}
                options={[
                  { value: 'company', label: 'Company default' },
                  { value: 'project', label: 'Project focused' },
                  { value: 'selected', label: 'Selected sources only' },
                ]}
                fullWidth
              />
              <div className="company-ai-result-field">
                <div className="company-ai-result-label">Indexed sources</div>
                <div className="company-ai-result-value">{readySources.length} ready to cite</div>
              </div>
            </div>

            {sourceScope === 'selected' ? (
              readySources.length ? (
                <div className="company-ai-checkbox-list">
                  {readySources.map((source) => (
                    <label key={source.aiKnowledgeSourceId} className="company-ai-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedSourceIds.includes(source.aiKnowledgeSourceId)}
                        onChange={() => toggleSelectedSource(source.aiKnowledgeSourceId)}
                        style={{ marginRight: '10px' }}
                      />
                      <strong>{source.title}</strong>
                      <div className="company-ai-muted">
                        {source.sourceType.replace(/_/g, ' ')}
                        {' · '}
                        {source.description || source.folderPath || source.filePath || 'No description'}
                      </div>
                    </label>
                  ))}
                </div>
              ) : (
                <EmptyState
                  icon="📚"
                  title="No ready sources yet"
                  description="Ask an admin to index SharePoint folders or upload a reference file in AI Admin."
                />
              )
            ) : null}
          </FormSection>

          <Card
            header={(
              <CardHeader
                title="Router Preview"
                subtitle="This prefilter narrows the most relevant files and PMapp rows before anything is sent to the AI model."
                actions={(
                  <StatusBadge status={loadingGroundingPreview ? 'processing' : 'active'}>
                    {loadingGroundingPreview
                      ? 'Routing...'
                      : `${groundingPreview?.knowledgeFiles.length || 0} files · ${groundingPreview?.pmContext.length || 0} rows`}
                  </StatusBadge>
                )}
              />
            )}
          >
            {chatMessage.trim() ? (
              <div className="company-ai-stacked">
                <div className="company-ai-result-box">
                  <div className="company-ai-section-title" style={{ marginTop: 0 }}>What will be sent</div>
                  <div className="company-ai-muted">
                    The router groups chunk matches back into files, trims duplicate excerpts, and keeps the strongest PMapp rows so the answer starts from tighter grounding.
                  </div>
                  {groundingPreview?.intent ? (
                    <div className="company-ai-muted" style={{ marginTop: '10px' }}>
                      Intent: <strong>{groundingPreview.intent.primaryIntent.replace(/_/g, ' ')}</strong>
                      {' · '}
                      Live priority {groundingPreview.intent.liveDataPriority}
                      {' · '}
                      Knowledge priority {groundingPreview.intent.knowledgePriority}
                    </div>
                  ) : null}
                  {groundingPreview?.intent?.searchPhrases?.knowledge?.length || groundingPreview?.intent?.searchPhrases?.liveData?.length ? (
                    <div className="company-ai-source-strip" style={{ marginTop: '10px' }}>
                      {groundingPreview.intent?.searchPhrases.liveData.slice(0, 2).map((phrase) => (
                        <span key={`live-${phrase}`} className="company-ai-source-pill">PM: {phrase}</span>
                      ))}
                      {groundingPreview.intent?.searchPhrases.knowledge.slice(0, 2).map((phrase) => (
                        <span key={`knowledge-${phrase}`} className="company-ai-source-pill">File: {phrase}</span>
                      ))}
                    </div>
                  ) : null}
                </div>

                {groundingPreview?.knowledgeFiles.length ? (
                  <div className="company-ai-citation-list">
                    {groundingPreview.knowledgeFiles.map((file) => (
                      <div key={`${file.sourceId}-${file.filePath || file.fileName || file.sourceTitle}`} className="company-ai-citation">
                        <div className="company-ai-citation-title">{file.fileName || file.filePath || file.sourceTitle}</div>
                        <div className="company-ai-citation-copy">
                          {file.filePath || 'Path not available'}
                          {' · '}
                          {file.sourceTitle}
                          {' · '}
                          {file.excerptCount} excerpt{file.excerptCount === 1 ? '' : 's'} kept
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <EmptyState
                    icon="🧭"
                    title="No knowledge file matches yet"
                    description="Try a project number, folder name, workflow term, or file-related keyword to help the router find indexed files."
                  />
                )}

                {groundingPreview?.pmContext.length ? (
                  <>
                    <div className="company-ai-section-title">Matching PMapp Rows</div>
                    <div className="company-ai-action-list">
                      {groundingPreview.pmContext.map((item, index) => (
                        <div key={`${item.domain}-${item.recordId || index}`} className="company-ai-action">
                          <div className="company-ai-action-title">{item.title}</div>
                          <div className="company-ai-action-copy">{item.summary}</div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : null}
              </div>
            ) : (
              <EmptyState
                icon="🧭"
                title="Router preview appears while you type"
                description="Start a draft question and this panel will show the most relevant indexed files and PMapp rows before the AI answer is generated."
              />
            )}
          </Card>

          <Card
            header={(
              <CardHeader
                title="My Conversation History"
                subtitle="Only threads created under your signed-in account are listed here."
                actions={<StatusBadge status={String(conversations.length)}>{conversations.length} threads</StatusBadge>}
              />
            )}
          >
            {conversations.length ? (
              <div className="company-ai-list">
                {conversations.map((conversation) => (
                  <button
                    key={conversation.aiConversationId}
                    type="button"
                    className={`company-ai-list-button${conversation.aiConversationId === activeConversationId ? ' company-ai-list-button--active' : ''}`}
                    onClick={() => void handleSelectConversation(conversation.aiConversationId)}
                  >
                    <div className="company-ai-list-title">{conversation.title || 'Conversation'}</div>
                    <div className="company-ai-list-subtitle">
                      {conversation.sourceScope} scope
                      {' · '}
                      {formatDateTime(conversation.lastMessageAt || conversation.createdDate)}
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <EmptyState
                icon="💬"
                title="No AI conversations yet"
                description="Your first question will start a new thread automatically."
              />
            )}
          </Card>
        </div>
      </div>
    </PageSection>
  );

  const aiSearchTab = (
    <PageSection width="full" padding="default">
      <div className="company-ai-layout">
        <div className="company-ai-primary">
          <Card
            header={(
              <CardHeader
                title="PM Query"
                subtitle="This mode now uses the Phase 1A agent shell with grounded read-only PMapp tools, clarification, and visible execution steps."
                actions={<StatusBadge status={aiSearchResult ? 'active' : 'warning'}>{aiSearchResult ? aiSearchResult.actionType.replace(/_/g, ' ') : 'Ready'}</StatusBadge>}
              />
            )}
          >
            <div className="company-ai-stacked">
              <form onSubmit={handleRunAiSearch} className="company-ai-composer" style={{ paddingTop: 0, borderTop: 'none' }}>
                <Textarea
                  label={aiSearchResult?.actionType === 'clarify' ? 'Clarification reply' : 'PM query'}
                  value={aiSearchQuery}
                  onChange={(event) => setAiSearchQuery(event.target.value)}
                  placeholder={aiSearchResult?.actionType === 'clarify'
                    ? 'Example: Box Hill 那个'
                    : 'Example: Whitehorse 那个项目, Smith 那个最近报价后来有没有开票, or short task/quotation/address fragments.'}
                  rows={3}
                  density="compact"
                  fullWidth
                />
                <div className="company-ai-form-actions">
                  <Button type="submit" loading={runningAiSearch}>
                    {aiSearchResult?.actionType === 'clarify' ? 'Continue PM Query' : 'Run PM Query'}
                  </Button>
                  <span className="company-ai-muted">
                    Uses the single agent entrypoint with read-only PMapp grounding and draft-safe follow-up generation only.
                  </span>
                </div>
                {searchFeedback ? (
                  <div className={`company-ai-feedback company-ai-feedback--${searchFeedbackTone}`}>
                    {searchFeedback}
                  </div>
                ) : null}
              </form>

              {aiSearchResult ? (
                <div className="company-ai-stacked">
                  <div className="company-ai-source-strip">
                    <span className="company-ai-source-pill">Action: {aiSearchResult.actionType.replace(/_/g, ' ')}</span>
                    <span className="company-ai-source-pill">Confidence: {Math.round((aiSearchResult.confidence || 0) * 100)}%</span>
                    <span className="company-ai-source-pill">Session: {aiSearchResult.sessionId}</span>
                    <span className="company-ai-source-pill">{aiSearchResult.candidates.length} candidates</span>
                  </div>

                  <div className={`company-ai-result-box${aiSearchResult.actionType === 'clarify' ? ' company-ai-result-box--warning' : ''}`}>
                    <div className="company-ai-section-title" style={{ marginTop: 0 }}>
                      {aiSearchResult.actionType === 'clarify' ? 'Need One Detail' : 'Answer'}
                    </div>
                    {renderStructuredSectionBody(aiSearchResult.answer)}
                    {aiSearchResult.clarificationQuestion ? (
                      <div className="company-ai-muted" style={{ marginTop: '10px' }}>
                        {aiSearchResult.clarificationQuestion}
                      </div>
                    ) : null}
                  </div>

                  {aiSearchResult.agentData?.draftEmail ? (
                    <div className="company-ai-result-box">
                      <div className="company-ai-section-title" style={{ marginTop: 0 }}>Draft Output</div>
                      <div className="company-ai-message-body">
                        Subject: {aiSearchResult.agentData.draftEmail.subject}
                      </div>
                      {aiSearchResult.agentData.draftEmail.notes?.length ? (
                        <div className="company-ai-muted" style={{ marginTop: '10px' }}>
                          {aiSearchResult.agentData.draftEmail.notes.join(' · ')}
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  {aiSearchResult.actionType === 'clarify' && aiSearchResult.candidates.length ? (
                    <div className="company-ai-result-box company-ai-result-box--warning">
                      <div className="company-ai-section-title" style={{ marginTop: 0 }}>Closest Matches</div>
                      <div className="company-ai-message-body">
                        {aiSearchResult.candidates.slice(0, 3).map((candidate) => candidate.title).join('\n')}
                      </div>
                    </div>
                  ) : null}

                  <div className="company-ai-result-grid">
                    <div className="company-ai-result-field">
                      <div className="company-ai-result-label">Scope</div>
                      <div className="company-ai-result-value">{aiSearchResult.stateSummary.currentScope || 'mixed'}</div>
                    </div>
                    <div className="company-ai-result-field">
                      <div className="company-ai-result-label">Selected Entity</div>
                      <div className="company-ai-result-value">{aiSearchResult.selectedEntityType || 'None'}{aiSearchResult.selectedEntityId ? ` #${aiSearchResult.selectedEntityId}` : ''}</div>
                    </div>
                    <div className="company-ai-result-field">
                      <div className="company-ai-result-label">Date Range</div>
                      <div className="company-ai-result-value">
                        {aiSearchResult.plan.dateRange.startDate
                          ? `${aiSearchResult.plan.dateRange.startDate} .. ${aiSearchResult.plan.dateRange.endDate}`
                          : 'None'}
                      </div>
                    </div>
                    <div className="company-ai-result-field">
                      <div className="company-ai-result-label">Relation Hops</div>
                      <div className="company-ai-result-value">{aiSearchResult.plan.relationHops.join(', ') || 'None'}</div>
                    </div>
                    <div className="company-ai-result-field">
                      <div className="company-ai-result-label">Executed Steps</div>
                      <div className="company-ai-result-value">{aiSearchResult.agentSteps?.length || 0}</div>
                    </div>
                    <div className="company-ai-result-field">
                      <div className="company-ai-result-label">Grounded Sources</div>
                      <div className="company-ai-result-value">{aiSearchResult.citations?.length || 0}</div>
                    </div>
                  </div>
                </div>
              ) : (
                <EmptyState
                  icon="🔎"
                  title="Run a PM query"
                  description="This mode is best for grounded PMapp lookups that may need clarification, candidate ranking, or a draft-safe payment follow-up output."
                />
              )}
            </div>
          </Card>
        </div>

        <div className="company-ai-sidebar">
          <Card
            header={(
              <CardHeader
                title="Candidate Panel"
                subtitle="Each result shows the top PM candidates and their score breakdowns."
                actions={<StatusBadge status={String(aiSearchResult?.candidates.length || 0)}>{aiSearchResult?.candidates.length || 0} candidates</StatusBadge>}
              />
            )}
          >
            {aiSearchResult?.candidates.length ? (
              <div className="company-ai-citation-list">
                {aiSearchResult.candidates.map((item, index) => (
                  <div key={`ai-search-candidate-${index}`} className="company-ai-citation">
                    <div className="company-ai-citation-title">{item.title}</div>
                    <div className="company-ai-citation-copy">
                      {item.subtitle || 'No summary'}
                      {item.score !== undefined ? ` · score ${Math.round(item.score)}` : ''}
                      {item.confidence !== undefined ? ` · confidence ${Math.round((item.confidence || 0) * 100)}%` : ''}
                    </div>
                    {item.scoreBreakdown?.length ? (
                      <div className="company-ai-muted" style={{ marginTop: '6px' }}>
                        {item.scoreBreakdown.slice(0, 4).map((part) => `${part.reason}: ${part.score}`).join(' · ')}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState
                icon="🧾"
                title="No candidates yet"
                description="After PM Query runs, the top PM candidates and their score clues will appear here."
              />
            )}
          </Card>
          {aiSearchResult?.agentSteps?.length ? (
            <Card
              header={(
                <CardHeader
                  title="Execution Trail"
                  subtitle="Each step is shown so PM Query stays transparent about what grounded data it used."
                  actions={<StatusBadge status={String(aiSearchResult.agentSteps.length)}>{aiSearchResult.agentSteps.length} steps</StatusBadge>}
                />
              )}
            >
              <div className="company-ai-action-list">
                {aiSearchResult.agentSteps.map((step, index) => (
                  <div key={`agent-step-${index}`} className="company-ai-action">
                    <div className="company-ai-action-title">
                      Step {index + 1} · {step.tool.replace(/_/g, ' ')}
                    </div>
                    <div className="company-ai-action-copy">{step.summary}</div>
                  </div>
                ))}
              </div>
            </Card>
          ) : null}
          {aiSearchResult?.citations?.length ? (
            <Card
              header={(
                <CardHeader
                  title="Grounded Sources"
                  subtitle="These are the live PMapp records or draft artifacts the agent shell surfaced for this answer."
                  actions={<StatusBadge status={String(aiSearchResult.citations.length)}>{aiSearchResult.citations.length} sources</StatusBadge>}
                />
              )}
            >
              <div className="company-ai-citation-list">
                {aiSearchResult.citations.map((citation, index) => (
                  <div key={`agent-citation-${index}`} className="company-ai-citation">
                    <div className="company-ai-citation-title">
                      {citation.label}
                      <span className="company-ai-muted"> · {citation.sourceType === 'draft' ? 'Draft' : 'Live PMapp'}</span>
                    </div>
                    <div className="company-ai-citation-copy">
                      {citation.detail || 'No additional source detail.'}
                      {citation.routePath ? ` (${citation.routePath})` : ''}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          ) : null}
        </div>
      </div>
    </PageSection>
  );

  const onlineQuoteFilesTab = (
    <PageSection width="full" padding="default">
      <div className="company-ai-layout quote-workspace">
        <div className="company-ai-sidebar quote-workspace__requests">
          <Card
            header={(
              <CardHeader
                title="Record Groups"
                subtitle="Each group is one online quote request."
                actions={<StatusBadge status={String(filteredOnlineQuoteFileGroups.length)}>{filteredOnlineQuoteFileGroups.length} groups</StatusBadge>}
              />
            )}
          >
            {filteredOnlineQuoteFileGroups.length ? (
              <div className="company-ai-list">
                {filteredOnlineQuoteFileGroups.map((group) => (
                  <button
                    key={group.referralLeadId}
                    type="button"
                    className={`company-ai-list-button${selectedOnlineQuoteGroup?.referralLeadId === group.referralLeadId ? ' company-ai-list-button--active' : ''}`}
                    aria-pressed={selectedOnlineQuoteGroup?.referralLeadId === group.referralLeadId}
                    onClick={() => { setSelectedOnlineQuoteGroupId(group.referralLeadId); setSelectedQuotePreviewId(null); }}
                  >
                    <div className="company-ai-list-title">{buildOnlineQuoteGroupTitle(group)}</div>
                    <div className="company-ai-list-subtitle">
                      {buildOnlineQuoteSiteAddress(group, 'No address')}
                      {' · '}
                      {formatDateTime(group.submittedDate)}
                    </div>
                    <div className="company-ai-source-strip" style={{ marginTop: '8px' }}>
                      {isQuarantinedOnlineQuoteGroup(group) ? <span className="company-ai-source-pill company-ai-source-pill--warning">Quarantined</span> : null}
                      <span className="company-ai-source-pill">{group.files.length} file{group.files.length === 1 ? '' : 's'}</span>
                      {group.customerType ? <span className="company-ai-source-pill">{group.customerType}</span> : null}
                      {group.helpType ? <span className="company-ai-source-pill">{group.helpType}</span> : null}
                      {group.suburb ? <span className="company-ai-source-pill">{group.suburb}</span> : null}
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <EmptyState
                icon="🗂️"
                title="No matching groups"
                description="Try a broader quote request, client, address, suburb, or file search."
              />
            )}
          </Card>

          <Card
            header={(
              <CardHeader
                title="Upload Coverage"
                subtitle="Current online quote request upload inventory."
                actions={<StatusBadge status={String(onlineQuoteFileCount)}>{onlineQuoteFileCount} files</StatusBadge>}
              />
            )}
          >
            <div className="company-ai-inline-metrics">
              <div className="company-ai-inline-metric">
                <strong>{onlineQuoteFileGroups.length}</strong>
                <span>request groups</span>
              </div>
              <div className="company-ai-inline-metric">
                <strong>{onlineQuoteFileCount}</strong>
                <span>linked files</span>
              </div>
              <div className="company-ai-inline-metric">
                <strong>{onlineQuoteFileGroups.filter((group) => group.quotationId).length}</strong>
                <span>quoted</span>
              </div>
            </div>
          </Card>
        </div>

        <div className="company-ai-primary quote-workspace__detail">
          <Card
            header={(
              <CardHeader
                title="Online Quote Files"
                subtitle="Portal quote request uploads grouped by request record."
                actions={<StatusBadge status={String(filteredOnlineQuoteFileCount)}>{filteredOnlineQuoteFileCount} files</StatusBadge>}
              />
            )}
          >
            <div className="company-ai-stacked">
              <div className="company-ai-form-grid">
                <SearchInput
                  value={onlineQuoteFileFilter}
                  onChange={(event) => setOnlineQuoteFileFilter(event.target.value)}
                  onClear={() => setOnlineQuoteFileFilter('')}
                  placeholder="Filter by request code, client, address, suburb, or file"
                  density="compact"
                  fullWidth
                />
                <div className="company-ai-form-actions" style={{ justifyContent: 'flex-start' }}>
                  <Button type="button" variant="secondary" loading={onlineQuoteFileLoading} onClick={() => void handleRefreshOnlineQuoteFiles()}>
                    Refresh Files
                  </Button>
                  <Button
                    type="button"
                    loading={onlineQuoteQuotationBusy}
                    disabled={!selectedOnlineQuoteGroup || selectedOnlineQuoteGroupQuarantined}
                    onClick={() => void handleCreateQuotationFromOnlineQuoteGroup()}
                  >
                    Create Quotation
                  </Button>
                </div>
              </div>

              {onlineQuoteFileFeedback ? (
                <div className={`company-ai-feedback company-ai-feedback--${onlineQuoteFileFeedbackTone}`}>
                  {onlineQuoteFileFeedback}
                </div>
              ) : null}

              {selectedOnlineQuoteGroup ? (
                <div className="company-ai-stacked">
                  <div className="company-ai-result-box">
                    <div className="company-ai-record-title">
                      {buildOnlineQuoteGroupTitle(selectedOnlineQuoteGroup)}
                    </div>
                    <div className="company-ai-muted">
                      {buildOnlineQuoteSiteAddress(selectedOnlineQuoteGroup)}
                    </div>
                    <div className="company-ai-source-strip" style={{ marginTop: '10px' }}>
                      {selectedOnlineQuoteGroupQuarantined ? <span className="company-ai-source-pill company-ai-source-pill--warning">Quarantined</span> : null}
                      {selectedOnlineQuoteGroup.requestCode ? <span className="company-ai-source-pill">Request {selectedOnlineQuoteGroup.requestCode}</span> : null}
                      <span className="company-ai-source-pill">Lead #{selectedOnlineQuoteGroup.referralLeadId}</span>
                      {selectedOnlineQuoteGroup.customerType ? <span className="company-ai-source-pill">I am a: {selectedOnlineQuoteGroup.customerType}</span> : null}
                      {selectedOnlineQuoteGroup.helpType ? <span className="company-ai-source-pill">Need: {selectedOnlineQuoteGroup.helpType}</span> : null}
                      {selectedOnlineQuoteGroup.projectType ? <span className="company-ai-source-pill">Project: {selectedOnlineQuoteGroup.projectType}</span> : null}
                      {selectedOnlineQuoteGroup.referralStatus ? <span className="company-ai-source-pill">{selectedOnlineQuoteGroup.referralStatus}</span> : null}
                      {selectedOnlineQuoteGroup.reviewStatus ? <span className="company-ai-source-pill">Review: {selectedOnlineQuoteGroup.reviewStatus}</span> : null}
                      {selectedOnlineQuoteGroup.quotationId ? <span className="company-ai-source-pill">Quotation #{selectedOnlineQuoteGroup.quotationId}</span> : null}
                      {selectedOnlineQuoteGroup.projectId ? <span className="company-ai-source-pill">Project #{selectedOnlineQuoteGroup.projectId}</span> : null}
                    </div>
                  </div>

                  {selectedOnlineQuoteGroupQuarantined ? (
                    <div className="company-ai-feedback company-ai-feedback--warning">
                      Quarantined website quote request. Notification email and referral workflow creation were suppressed.
                      {selectedOnlineQuoteGroup.spamReasons?.length ? ` Reasons: ${selectedOnlineQuoteGroup.spamReasons.join(', ')}.` : ''}
                      {typeof selectedOnlineQuoteGroup.spamScore === 'number' ? ` Score: ${selectedOnlineQuoteGroup.spamScore}.` : ''}
                    </div>
                  ) : null}

                  <section className="quote-message" aria-label="Message preview">
                    <div className="quote-message__heading"><h3>Message Preview</h3><span className="company-ai-muted">Customer submission · {formatDateTime(selectedOnlineQuoteGroup.submittedDate)}</span></div>
                    <p>{selectedOnlineQuoteGroup.notes?.trim() ? selectedOnlineQuoteGroup.notes : 'No message was included with this quote request.'}</p>
                  </section>

                  <div className="quote-review">
                    <section className="quote-review__files" aria-label="Request attachments">
                      <h3>Attachments <span className="company-ai-muted">({selectedOnlineQuoteGroup.files.length})</span></h3>
                      {selectedOnlineQuoteGroup.files.length ? (
                        <div className="company-ai-citation-list">
                          {selectedOnlineQuoteGroup.files.map((file) => (
                            <div key={file.referralLeadFileId} className="company-ai-citation">
                              <div className="company-ai-citation-title">
                                <button type="button" className="quote-file-select" aria-pressed={selectedOnlineQuotePreviewFile?.referralLeadFileId === file.referralLeadFileId} onClick={() => setSelectedQuotePreviewId(file.referralLeadFileId)}>{file.fileName}</button>
                                {file.isPrimary ? <span className="company-ai-muted"> · primary</span> : null}
                              </div>
                              <div className="company-ai-citation-copy">
                                {formatFileSize(file.fileSizeBytes)}
                                {' · '}
                                {file.contentType || 'application/octet-stream'}
                                {' · '}
                                Uploaded {formatDateTime(file.uploadedDate)}
                              </div>
                              <div className="company-ai-source-strip" style={{ marginTop: '8px' }}>
                                {file.storageProvider ? <span className="company-ai-source-pill">{file.storageProvider}</span> : null}
                                {file.groupPath ? <span className="company-ai-source-pill">{file.groupPath}</span> : null}
                                {file.blobName ? <span className="company-ai-source-pill">Blob listed</span> : null}
                                {file.draftFileId ? <span className="company-ai-source-pill">SQL link</span> : null}
                              </div>
                              {file.contentPath ? (
                                <div className="company-ai-form-actions" style={{ marginTop: '10px', justifyContent: 'flex-start' }}>
                                  <a href={file.contentPath} download={file.fileName || undefined} className="company-ai-link">
                                    {getOnlineQuoteFileActionLabel(file)}
                                  </a>
                                </div>
                              ) : (
                                <div className="company-ai-feedback company-ai-feedback--warning" style={{ marginTop: '10px' }}>
                                  This file row has no draft blob id.
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <EmptyState
                          icon="📎"
                          title="No files on this request"
                          description="This quote request group has no linked upload rows."
                        />
                      )}
                    </section>
                  </div>
                </div>
              ) : (
                <EmptyState
                  icon="📎"
                  title="No online quote files found"
                  description={onlineQuoteFileGroups.length
                    ? 'Clear the filter or search for another request, client, address, suburb, or file name.'
                    : 'Files from the website quote upload container will appear here once the backend can list the blob container.'}
                />
              )}
            </div>
          </Card>
        </div>
        <aside className="quote-workspace__preview" aria-label="Attachment preview pane">
          <OnlineQuoteFilePreview key={selectedOnlineQuoteGroup?.referralLeadId || 'empty'} file={selectedOnlineQuotePreviewFile} />
        </aside>
      </div>
    </PageSection>
  );

  const fileDocumentTab = (
    <PageSection width="full" padding="default">
      <div className="company-ai-stacked">
        <FormSection
          title="Build Company Knowledge Base"
          description="This tab now focuses on what Company AI is allowed to read. SharePoint routing stays paused in the UI until that workflow feels safer."
          icon="📚"
          accentColor="#2251b4"
          actions={<StatusBadge status="warning">Knowledge-first mode</StatusBadge>}
        >
          <div className="company-ai-stacked">
            <div className="company-ai-result-box company-ai-result-box--warning">
              <div className="company-ai-section-title" style={{ marginTop: 0 }}>Current mode</div>
              <div className="company-ai-muted">
                The backend preview and commit APIs remain in the codebase, but this workspace no longer exposes document sorting or SharePoint filing actions here.
                The safer first step is to make the knowledge base explicit so everyone can see which files Company AI is allowed to use in answers.
              </div>
            </div>

            <div className="company-ai-inline-metrics">
              <div className="company-ai-inline-metric">
                <strong>{knowledgeSources.length}</strong>
                <span>registered sources</span>
              </div>
              <div className="company-ai-inline-metric">
                <strong>{readySources.length}</strong>
                <span>ready for AI answers</span>
              </div>
              <div className="company-ai-inline-metric">
                <strong>{pendingSources.length}</strong>
                <span>still indexing</span>
              </div>
              <div className="company-ai-inline-metric">
                <strong>{erroredSources.length}</strong>
                <span>need attention</span>
              </div>
            </div>

            <div className="company-ai-form-actions">
              {canManageKnowledgeSources ? (
                <Button type="button" onClick={() => navigate('/ai/admin')}>
                  Open AI Admin
                </Button>
              ) : null}
              <span className="company-ai-muted">
                Admin users add SharePoint folders, SharePoint files, or uploaded reference files in AI Admin. Once indexing finishes, those sources become available to cite in chat.
              </span>
            </div>
          </div>
        </FormSection>

        <FormSection
          title="How Company AI Uses Files"
          description="These rules make the current knowledge path more predictable."
          icon="🧭"
          accentColor="#2251b4"
        >
          <div className="company-ai-knowledge-grid">
            <div className="company-ai-knowledge-card">
              <div className="company-ai-knowledge-step">1</div>
              <div className="company-ai-knowledge-title">Only registered sources are used</div>
              <div className="company-ai-knowledge-copy">
                Company AI does not read your whole SharePoint site by default. It only uses sources that were explicitly registered and successfully indexed.
              </div>
            </div>
            <div className="company-ai-knowledge-card">
              <div className="company-ai-knowledge-step">2</div>
              <div className="company-ai-knowledge-title">SharePoint folders are indexed recursively</div>
              <div className="company-ai-knowledge-copy">
                When a SharePoint folder is added as a source, the indexer walks through files inside that folder tree, extracts text, and stores searchable chunks together with file names and file paths.
              </div>
            </div>
            <div className="company-ai-knowledge-card">
              <div className="company-ai-knowledge-step">3</div>
              <div className="company-ai-knowledge-title">Chat answers search the index, not live folders</div>
              <div className="company-ai-knowledge-copy">
                In Company AI, the assistant searches indexed knowledge chunks plus read-only PMapp data. It can cite the matching file path, but it is not live-browsing arbitrary SharePoint folders during each question.
              </div>
            </div>
            <div className="company-ai-knowledge-card">
              <div className="company-ai-knowledge-step">4</div>
              <div className="company-ai-knowledge-title">What is not used automatically</div>
              <div className="company-ai-knowledge-copy">
                Unregistered SharePoint folders, arbitrary uploaded documents, and direct SharePoint filing are intentionally out of the normal workspace flow right now.
              </div>
            </div>
          </div>
        </FormSection>

        <FormSection
          title="Good First Knowledge Sources"
          description="These are the kinds of sources that will make Company AI more useful quickly."
          icon="🗃️"
          accentColor="#2251b4"
        >
          <div className="company-ai-knowledge-grid company-ai-knowledge-grid--wide">
            <div className="company-ai-knowledge-card">
              <div className="company-ai-knowledge-title">CivilAndStructural folder</div>
              <div className="company-ai-knowledge-copy">
                Add the CivilAndStructural SharePoint folder when you want Company AI to answer from engineering reference material, reusable standards, or internal delivery documents stored there.
              </div>
            </div>
            <div className="company-ai-knowledge-card">
              <div className="company-ai-knowledge-title">Quotation folder</div>
              <div className="company-ai-knowledge-copy">
                Add the Quotation folder when you want the assistant to cite quotation guidance, proposal templates, or commercial reference files rather than only relying on PMapp summaries.
              </div>
            </div>
            <div className="company-ai-knowledge-card">
              <div className="company-ai-knowledge-title">Policies, checklists, and workflow guides</div>
              <div className="company-ai-knowledge-copy">
                Company handbooks, filing guides, and process notes are strong early sources because they produce high-confidence citations and reduce vague answers.
              </div>
            </div>
          </div>
        </FormSection>

        <Card
          header={(
            <CardHeader
              title="Indexed File Inventory"
              subtitle="This is the file-level window for what the knowledge base has actually indexed so far."
              actions={<StatusBadge status={String(filteredKnowledgeFiles.length)}>{filteredKnowledgeFiles.length} files</StatusBadge>}
            />
          )}
        >
          <div className="company-ai-stacked">
            <div className="company-ai-form-grid">
              <SearchInput
                value={knowledgeFileFilter}
                onChange={(event) => setKnowledgeFileFilter(event.target.value)}
                onClear={() => setKnowledgeFileFilter('')}
                placeholder="Filter by file name, path, or source title"
                density="compact"
                fullWidth
              />
              <div className="company-ai-result-field">
                <div className="company-ai-result-label">Inventory rule</div>
                <div className="company-ai-result-value">
                  Only files extracted into the SQL knowledge index appear here. If a SharePoint folder exists but its files are not listed yet, indexing has not completed for that content.
                </div>
              </div>
            </div>

            {filteredKnowledgeFiles.length ? (
              <div className="company-ai-list company-ai-list--inventory">
                {filteredKnowledgeFiles.map((file) => (
                  <div key={`${file.sourceId}-${file.filePath || file.fileName || file.sourceTitle}`} className="company-ai-list-button" style={{ cursor: 'default' }}>
                    <div className="company-ai-list-title">{file.fileName || file.filePath || file.sourceTitle}</div>
                    <div className="company-ai-list-subtitle">
                      {file.filePath || 'Path not available'}
                      {' · '}
                      {file.sourceTitle}
                    </div>
                    <div className="company-ai-source-strip" style={{ marginTop: '8px' }}>
                      <span className="company-ai-source-pill">{file.sourceType.replace(/_/g, ' ')}</span>
                      <span className="company-ai-source-pill">{file.scopeType} scope</span>
                      <span className="company-ai-source-pill">{file.chunkCount} chunk{file.chunkCount === 1 ? '' : 's'}</span>
                    </div>
                    <div className="company-ai-muted" style={{ marginTop: '10px' }}>
                      Indexed {formatDateTime(file.lastIndexedAt)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState
                icon="🗂️"
                title="No indexed files match this filter"
                description={knowledgeFiles.length
                  ? 'Try a broader file or folder keyword, or clear the filter to see the current indexed inventory.'
                  : 'Once sources are indexed, their individual file paths will appear here.'}
              />
            )}
          </div>
        </Card>

        <Card
          header={(
            <CardHeader
              title="Current Indexed Sources"
              subtitle="These are the sources the workspace can actually use once they are ready."
              actions={<StatusBadge status={String(readySources.length)}>{readySources.length} ready</StatusBadge>}
            />
          )}
        >
          {knowledgeSources.length ? (
            <div className="company-ai-list company-ai-list--static">
              {knowledgeSources.map((source) => (
                <div key={source.aiKnowledgeSourceId} className="company-ai-list-button company-ai-list-button--active" style={{ cursor: 'default' }}>
                  <div className="company-ai-list-title">{source.title}</div>
                  <div className="company-ai-list-subtitle">
                    {source.sourceType.replace(/_/g, ' ')}
                    {' · '}
                    {source.status}
                    {' · '}
                    Indexed {formatDateTime(source.lastIndexedAt)}
                  </div>
                  <div className="company-ai-source-strip" style={{ marginTop: '8px' }}>
                    {source.scopeType ? <span className="company-ai-source-pill">{source.scopeType} scope</span> : null}
                    {source.folderPath ? <span className="company-ai-source-pill">{source.folderPath}</span> : null}
                    {source.filePath ? <span className="company-ai-source-pill">{source.filePath}</span> : null}
                  </div>
                  <div className="company-ai-muted" style={{ marginTop: '10px' }}>
                    {source.description || source.folderPath || source.filePath || 'No description added yet.'}
                  </div>
                  {source.lastError ? (
                    <div className="company-ai-feedback company-ai-feedback--error" style={{ marginTop: '12px' }}>
                      {source.lastError}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          ) : (
            <EmptyState
              icon="📚"
              title="No knowledge sources registered yet"
              description="Start by adding a SharePoint folder or reference file in AI Admin. Once indexing finishes, Company AI can cite those files in answers."
            />
          )}
        </Card>
      </div>
    </PageSection>
  );

  const ifcModelsTab = (
    <PageSection width="full" padding="default">
      <div className="company-ai-stacked">
        {canManageIfcModels ? (
          <IfcAdminUploadPanel />
        ) : (
          <EmptyState
            icon="🔒"
            title="IFC model management is limited"
            description="Only PM or admin users can upload IFC models for the public viewer."
          />
        )}
      </div>
    </PageSection>
  );

  const projectFilesTab = (
    <PageSection width="full" padding="default">
      <div className="company-ai-stacked">
        {canManageProjectFiles ? (
          <ProjectFilesAdminPanel />
        ) : (
          <EmptyState
            icon="🔒"
            title="Project file management is limited"
            description="Only PM or admin users can upload project files for the client portal."
          />
        )}
      </div>
    </PageSection>
  );

  const dbydTab = (
    <PageSection width="full" padding="default">
      <div className="company-ai-stacked">
        <FormSection
          title="BYDA Automation"
          description="Before You Dig Australia now has a dedicated workspace tab so the team can track PMApp-driven enquiries without leaving the AI area."
          icon="🦺"
          accentColor="#2251b4"
          actions={<StatusBadge status={String(bydaEnquiries.length)}>{bydaEnquiries.length} records</StatusBadge>}
        >
          <div className="company-ai-stacked">
            <div className="company-ai-result-box company-ai-result-box--warning">
              <div className="company-ai-section-title" style={{ marginTop: 0 }}>Current rollout</div>
              <div className="company-ai-muted">
                PMApp can now lodge BYDA enquiries, refresh response activity, inspect returned result files, and file those results into the linked project SharePoint folder without leaving the workspace.
              </div>
            </div>

            <BydaUsageSummary
              policy={bydaPolicy}
              heading="Usage limits"
              subheading="Daily per-person limits are capped at 3, the shared team cap is 10 per day, and the monthly cap is 50."
            />

            <div className="company-ai-inline-metrics">
              <div className="company-ai-inline-metric">
                <strong>{bydaEnquiries.length}</strong>
                <span>total records</span>
              </div>
              <div className="company-ai-inline-metric">
                <strong>{bydaSubmittedCount}</strong>
                <span>submitted or synced</span>
              </div>
              <div className="company-ai-inline-metric">
                <strong>{bydaRemoteCount}</strong>
                <span>remote enquiry ids</span>
              </div>
              <div className="company-ai-inline-metric">
                <strong>{bydaFailedCount}</strong>
                <span>failed requests</span>
              </div>
            </div>
          </div>
        </FormSection>

        <Card
          header={(
            <CardHeader
              title="New BYDA Enquiry"
              subtitle="Search for the project by number, name, or address, then capture the enquiry area from the workspace."
              actions={selectedBydaProject ? <StatusBadge status="ready">{selectedBydaProject.projectCode || selectedBydaProject.projectName}</StatusBadge> : undefined}
            />
          )}
        >
          <div className="company-ai-stacked">
            <div style={{ position: 'relative' }}>
              <label style={{ display: 'grid', gap: '6px' }}>
                <span className="company-ai-section-title" style={{ marginTop: 0, fontSize: '0.95rem' }}>Project search</span>
                <input
                  aria-label="Search projects for BYDA"
                  value={bydaProjectSearchQuery}
                  onChange={(event) => {
                    setBydaProjectSearchQuery(event.target.value);
                    if (selectedBydaProject && event.target.value !== buildProjectSearchValue(selectedBydaProject)) {
                      setSelectedBydaProject(null);
                      setBydaCreateDraft(null);
                    }
                    setBydaCreateFeedback(null);
                  }}
                  placeholder="Start typing a project number, address, suburb, or name"
                  style={bydaInputStyle}
                />
              </label>

              {bydaProjectSearchLoading ? (
                <div className="company-ai-muted" style={{ marginTop: '10px' }}>Searching projects...</div>
              ) : null}
              {bydaProjectSearchError ? (
                <div className="company-ai-feedback company-ai-feedback--error" style={{ marginTop: '10px' }}>
                  {bydaProjectSearchError}
                </div>
              ) : null}

              {!bydaProjectSearchLoading && bydaProjectOptions.length ? (
                <div className="company-ai-list company-ai-list--static" style={{ marginTop: '12px' }}>
                  {bydaProjectOptions.map((project) => {
                    const notice = buildBydaExistingProjectNotice(findBydaProjectRecord(
                      project,
                      bydaProjectEnquiryLookup[project.projectId] || bydaEnquiries
                    ));
                    return (
                      <button
                        key={project.projectId}
                        type="button"
                        className="company-ai-list-button company-ai-list-button--active"
                        onClick={() => handleSelectBydaProject(project)}
                        style={{ textAlign: 'left' }}
                      >
                        <div className="company-ai-list-title">
                          {project.projectCode || 'Project'} {project.projectName ? `- ${project.projectName}` : ''}
                        </div>
                        <div className="company-ai-list-subtitle">{buildProjectLocationLabel(project)}</div>
                        {notice ? (
                          <div className="company-ai-feedback company-ai-feedback--warning" style={{ marginTop: '10px' }}>
                            {notice}
                          </div>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </div>

            {selectedBydaProject && bydaCreateDraft ? (
              <div className="company-ai-stacked">
                <div className="company-ai-result-box">
                  <div className="company-ai-section-title" style={{ marginTop: 0 }}>
                    {selectedBydaProject.projectCode || 'Project'} {selectedBydaProject.projectName ? `- ${selectedBydaProject.projectName}` : ''}
                  </div>
                  <div className="company-ai-muted">{buildProjectLocationLabel(selectedBydaProject)}</div>
                  {selectedBydaProjectNotice ? (
                    <div className="company-ai-feedback company-ai-feedback--warning" style={{ marginTop: '12px' }}>
                      {selectedBydaProjectNotice}
                    </div>
                  ) : null}
                  <div className="company-ai-actions-row" style={{ marginTop: '12px', gap: '12px', flexWrap: 'wrap' }}>
                    <Button variant="secondary" onClick={resetBydaCreateSelection} disabled={bydaCreateSubmitting}>
                      Change Project
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setBydaCreateDraft(createBydaDraftFromProject({
                          projectId: selectedBydaProject.projectId,
                          projectCode: selectedBydaProject.projectCode,
                          siteAddress: selectedBydaProject.siteAddress,
                          suburb: selectedBydaProject.suburb,
                          postCode: selectedBydaProject.postCode,
                        }));
                        setBydaCreateAutoLocateKey((current) => current + 1);
                      }}
                      disabled={bydaCreateSubmitting}
                    >
                      Reset Form
                    </Button>
                  </div>
                </div>

                <div
                  style={{
                    display: 'grid',
                    gap: '16px',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                  }}
                >
                  <label style={{ display: 'grid', gap: '6px' }}>
                    <span>User reference</span>
                    <input
                      aria-label="BYDA user reference"
                      value={bydaCreateDraft.userReference}
                      onChange={(event) => setBydaCreateField('userReference', event.target.value)}
                      style={bydaInputStyle}
                    />
                  </label>
                  <label style={{ display: 'grid', gap: '6px' }}>
                    <span>Dig start</span>
                    <input
                      aria-label="BYDA dig start"
                      type="date"
                      value={bydaCreateDraft.digStartAt}
                      onChange={(event) => setBydaCreateField('digStartAt', event.target.value)}
                      style={bydaInputStyle}
                    />
                  </label>
                  <label style={{ display: 'grid', gap: '6px' }}>
                    <span>Dig end</span>
                    <input
                      aria-label="BYDA dig end"
                      type="date"
                      value={bydaCreateDraft.digEndAt}
                      onChange={(event) => setBydaCreateField('digEndAt', event.target.value)}
                      style={bydaInputStyle}
                    />
                  </label>
                  <label style={{ display: 'grid', gap: '6px' }}>
                    <span>Timezone</span>
                    <input
                      aria-label="BYDA timezone"
                      value={bydaCreateDraft.userTimezone}
                      onChange={(event) => setBydaCreateField('userTimezone', event.target.value)}
                      style={bydaInputStyle}
                    />
                  </label>
                  <Select
                    label="Enquiry type"
                    aria-label="BYDA enquiry type"
                    value={bydaCreateDraft.isPlanningJob ? 'planning' : 'excavation'}
                    onChange={(event) => setBydaCreateField('isPlanningJob', event.target.value === 'planning')}
                    options={[
                      { value: 'planning', label: 'Design' },
                      { value: 'excavation', label: 'Excavation' },
                    ]}
                    helpText="Default to Design unless excavation is actually scheduled."
                    fullWidth
                  />
                </div>

                <div
                  style={{
                    display: 'grid',
                    gap: '16px',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                  }}
                >
                  <fieldset style={{ border: 'none', padding: 0, margin: 0, display: 'grid', gap: '8px' }}>
                    <legend style={{ fontWeight: 600 }}>Activity types</legend>
                    {bydaFormOptionsLoading ? (
                      <div className="company-ai-muted">Loading BYDA activity options...</div>
                    ) : bydaFormOptions?.activityTypes?.length ? (
                      <div style={bydaOptionGridStyle}>
                        {bydaFormOptions.activityTypes.map((option) => (
                          <label key={option.code} style={bydaOptionCardStyle}>
                            <input
                              type="checkbox"
                              aria-label={formatBydaOptionLabel(option)}
                              checked={hasBydaSelection(bydaCreateDraft.activityTypes, option.code)}
                              onChange={(event) => updateBydaCreateCodeSelection('activityTypes', option.code, event.target.checked)}
                            />
                            <span>{formatBydaOptionLabel(option)}</span>
                          </label>
                        ))}
                      </div>
                    ) : (
                      <div className="company-ai-muted">No BYDA activity options are available for this enquiry type.</div>
                    )}
                    <span className="company-ai-muted">
                      {bydaFormOptions?.activityDomain
                        ? `PMApp is using the official BYDA domain ${bydaFormOptions.activityDomain} for this enquiry type.`
                        : 'Choose one or more activities that match the selected enquiry type.'}
                    </span>
                  </fieldset>

                  <fieldset style={{ border: 'none', padding: 0, margin: 0, display: 'grid', gap: '8px' }}>
                    <legend style={{ fontWeight: 600 }}>Workplace location(s)</legend>
                    <div style={bydaOptionGridStyle}>
                      {BYDA_WORKPLACE_OPTIONS.map((option) => (
                        <label key={option.code} style={bydaOptionCardStyle}>
                          <input
                            type="checkbox"
                            aria-label={option.label}
                            checked={hasBydaSelection(bydaCreateDraft.workplaceSelections, option.code)}
                            onChange={(event) => updateBydaCreateWorkplaceSelection(option.code, event.target.checked)}
                          />
                          <span>{option.label}</span>
                        </label>
                      ))}
                    </div>
                    <span className="company-ai-muted">Tick every workplace surface that applies to this enquiry area.</span>
                  </fieldset>
                </div>

                {bydaFormOptionsError ? (
                  <div className="company-ai-feedback company-ai-feedback--error">
                    Failed to load BYDA options: {bydaFormOptionsError}
                  </div>
                ) : null}

                <label style={{ display: 'grid', gap: '6px' }}>
                  <span>Working on behalf of</span>
                  <select
                    aria-label="BYDA working on behalf of"
                    value={bydaCreateDraft.authorityId}
                    onChange={(event) => setBydaCreateField('authorityId', event.target.value)}
                    style={bydaInputStyle}
                  >
                    <option value="">Private</option>
                    {(bydaFormOptions?.authorityOptions || []).map((option) => (
                      <option key={option.id} value={String(option.id)}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <span className="company-ai-muted">
                    {bydaFormOptions?.authorityOptionsWarning
                      ? bydaFormOptions.authorityOptionsWarning
                      : bydaCreateShape
                        ? 'Matching organisations are loaded from BYDA for the current enquiry polygon.'
                        : 'Draw the enquiry area to load matching organisations. Leave as Private if not working on behalf of another organisation.'}
                  </span>
                </label>

                <label style={{ display: 'grid', gap: '6px' }}>
                  <span>Notes</span>
                  <textarea
                    aria-label="BYDA notes"
                    value={bydaCreateDraft.notes}
                    onChange={(event) => setBydaCreateField('notes', event.target.value)}
                    style={bydaTextareaStyle}
                  />
                </label>

                <div
                  style={{
                    display: 'grid',
                    gap: '16px',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                  }}
                >
                  <label style={{ display: 'grid', gap: '6px' }}>
                    <span>Address line 1</span>
                    <input
                      aria-label="BYDA address line 1"
                      value={bydaCreateDraft.addressLine1}
                      onChange={(event) => setBydaCreateField('addressLine1', event.target.value)}
                      style={bydaInputStyle}
                    />
                  </label>
                  <label style={{ display: 'grid', gap: '6px' }}>
                    <span>Locality</span>
                    <input
                      aria-label="BYDA locality"
                      value={bydaCreateDraft.locality}
                      onChange={(event) => setBydaCreateField('locality', event.target.value)}
                      style={bydaInputStyle}
                    />
                  </label>
                  <label style={{ display: 'grid', gap: '6px' }}>
                    <span>Postcode</span>
                    <input
                      aria-label="BYDA postcode"
                      value={bydaCreateDraft.postcode}
                      onChange={(event) => setBydaCreateField('postcode', event.target.value)}
                      style={bydaInputStyle}
                    />
                  </label>
                  <label style={{ display: 'grid', gap: '6px' }}>
                    <span>State</span>
                    <input
                      aria-label="BYDA state"
                      value={bydaCreateDraft.state}
                      onChange={(event) => setBydaCreateField('state', event.target.value)}
                      style={bydaInputStyle}
                    />
                  </label>
                </div>

                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      checked={Boolean(bydaCreateDraft.isSandboxTest)}
                      onChange={(event) => setBydaCreateField('isSandboxTest', event.target.checked)}
                    />
                    <span>Sandbox test</span>
                  </label>
                </div>

                <div style={{ display: 'grid', gap: '12px' }}>
                  <div>
                    <div className="company-ai-section-title" style={{ marginTop: 0 }}>Enquiry area</div>
                    <div className="company-ai-muted">Use the map to locate the project address, then click to draw the BYDA polygon.</div>
                  </div>

                  <BydaMapCapture
                    points={bydaCreateDraft.points}
                    addressQuery={bydaCreateAddressQuery}
                    autoLocateKey={bydaCreateAutoLocateKey || null}
                    disabled={bydaCreateSubmitting}
                    onChange={setBydaCreatePoints}
                  />
                </div>

                {bydaCreateFeedback ? (
                  <div className={`company-ai-feedback company-ai-feedback--${bydaCreateFeedbackTone === 'error' ? 'error' : 'success'}`}>
                    {bydaCreateFeedback}
                  </div>
                ) : null}

                <div className="company-ai-actions-row" style={{ gap: '12px', flexWrap: 'wrap' }}>
                  <Button onClick={handleCreateBydaEnquiry} disabled={bydaCreateSubmitting || bydaFormOptionsLoading || Boolean(bydaFormOptionsError)}>
                    {bydaCreateSubmitting ? 'Preparing BYDA...' : 'Review BYDA Request'}
                  </Button>
                </div>
              </div>
            ) : (
              <EmptyState
                icon="📍"
                title="Choose a project first"
                description="Search by project number, name, suburb, or street address to seed the BYDA enquiry form with the project address."
              />
            )}
          </div>
        </Card>

        <div className="company-ai-byda-workspace-grid">
          <Card
            header={(
              <CardHeader
                title="Recent BYDA Enquiries"
                subtitle="Select a BYDA enquiry to inspect returned files and push them into the project folder."
                actions={<StatusBadge status={String(bydaEnquiries.length)}>{bydaEnquiries.length} items</StatusBadge>}
              />
            )}
          >
            {bydaEnquiries.length ? (
              <div className="company-ai-list company-ai-list--static company-ai-list--byda-recent">
              {bydaEnquiries.map((item) => (
                <button
                  key={item.bydaEnquiryId}
                  type="button"
                  className="company-ai-list-button company-ai-list-button--active"
                  onClick={() => setSelectedBydaEnquiryId(item.bydaEnquiryId)}
                  style={{
                    textAlign: 'left',
                    border: selectedBydaEnquiryId === item.bydaEnquiryId ? '1px solid rgba(34, 81, 180, 0.35)' : undefined,
                    background: selectedBydaEnquiryId === item.bydaEnquiryId ? 'rgba(34, 81, 180, 0.08)' : undefined,
                  }}
                >
                  <div className="company-ai-list-title">
                    {item.project?.projectNumber || item.userReference || `BYDA ${item.bydaEnquiryId}`}
                  </div>
                  <div className="company-ai-list-subtitle">
                    {item.project?.projectName || item.project?.siteAddress || 'Project details pending'}
                  </div>
                  <div className="company-ai-source-strip" style={{ marginTop: '8px' }}>
                    <span className="company-ai-source-pill">Status: {formatBydaStatus(item.localStatus)}</span>
                    {item.remoteEnquiryId ? <span className="company-ai-source-pill">Remote #{item.remoteEnquiryId}</span> : null}
                    {item.remoteExternalId ? <span className="company-ai-source-pill">SYS-ID {item.remoteExternalId}</span> : null}
                    {item.project?.suburb ? <span className="company-ai-source-pill">{item.project.suburb}</span> : null}
                    {item.digStartAt ? <span className="company-ai-source-pill">Start {formatBydaDisplayDate(item.digStartAt)}</span> : null}
                    {item.digEndAt ? <span className="company-ai-source-pill">End {formatBydaDisplayDate(item.digEndAt)}</span> : null}
                  </div>
                  <div className="company-ai-muted" style={{ marginTop: '10px' }}>
                    {item.project?.siteAddress || 'No saved site address'}
                    {item.project?.council ? ` · ${item.project.council}` : ''}
                    {item.activityTypes.length ? ` · ${item.activityTypes.join(', ')}` : ''}
                  </div>
                  {item.lastErrorMessage ? (
                    <div className="company-ai-feedback company-ai-feedback--error" style={{ marginTop: '12px' }}>
                      {item.lastErrorMessage}
                    </div>
                  ) : null}
                  {item.shareUrl ? (
                    <div className="company-ai-muted" style={{ marginTop: '10px' }}>
                      Share link ready: {item.shareUrl}
                    </div>
                  ) : null}
                </button>
              ))}
              </div>
            ) : (
              <EmptyState
                icon="🦺"
                title="No BYDA enquiries logged yet"
                description="The BYDA workspace is ready for lodged enquiries. Once a project raises an enquiry, its responses and result files will appear here."
              />
            )}
          </Card>

          <Card
            header={(
              <CardHeader
                title="Selected BYDA Result Files"
                subtitle="Returned PDFs and attachments for the selected enquiry, plus the current SharePoint filing target."
                actions={bydaSelectedRecord ? <StatusBadge status={String(bydaResultDocuments.length)}>{bydaResultDocuments.length} files</StatusBadge> : undefined}
              />
            )}
          >
          {!bydaSelectedRecord ? (
            <EmptyState
              icon="📎"
              title="Choose a BYDA enquiry"
              description="Select an enquiry above to inspect its returned files and SharePoint filing status."
            />
          ) : (
            <div className="company-ai-stacked">
              <div className="company-ai-byda-result-summary-grid">
                <div className="company-ai-result-box">
                <div className="company-ai-section-title" style={{ marginTop: 0 }}>
                  {bydaSelectedRecord.project?.projectNumber || bydaSelectedRecord.userReference || `BYDA ${bydaSelectedRecord.bydaEnquiryId}`}
                </div>
                <div className="company-ai-muted">
                  {bydaSelectedRecord.project?.projectName || bydaSelectedRecord.project?.siteAddress || 'Project details pending'}
                </div>
                <div className="company-ai-source-strip" style={{ marginTop: '8px' }}>
                  <span className="company-ai-source-pill">Status: {formatBydaStatus(bydaSelectedRecord.localStatus)}</span>
                  {bydaSelectedRecord.remoteEnquiryId ? <span className="company-ai-source-pill">Remote #{bydaSelectedRecord.remoteEnquiryId}</span> : null}
                  {bydaSelectedRecord.remoteExternalId ? <span className="company-ai-source-pill">SYS-ID {bydaSelectedRecord.remoteExternalId}</span> : null}
                  {bydaDetail?.sharePointTarget?.folderPath ? <span className="company-ai-source-pill">{bydaDetail.sharePointTarget.folderPath}</span> : null}
                </div>
                <div className="company-ai-actions-row" style={{ marginTop: '12px', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <Button variant="secondary" onClick={() => void loadBydaDetail(bydaSelectedRecord.bydaEnquiryId, { refreshRemote: true })} disabled={bydaDetailLoading}>
                    {bydaDetailLoading ? 'Refreshing...' : 'Refresh Results'}
                  </Button>
                  <Button onClick={handleBydaFileResults} disabled={bydaFiling || bydaDetailLoading || bydaResultDocuments.length === 0}>
                    {bydaFiling ? 'Uploading To SharePoint...' : 'Upload Results To Project Folder'}
                  </Button>
                  <Button variant="secondary" onClick={handleFetchBydaYvwSewerInfo} disabled={bydaYvwSewerLoading || bydaDetailLoading}>
                    {bydaYvwSewerLoading ? 'Fetching Sewer Info...' : 'Fetch YVW Sewer Info'}
                  </Button>
                  {bydaSelectedRecord.shareUrl ? (
                    <a href={bydaSelectedRecord.shareUrl} target="_blank" rel="noreferrer" className="company-ai-link">
                      Open BYDA share link
                    </a>
                  ) : null}
                </div>
                {bydaFilingFeedback ? (
                  <div className={`company-ai-feedback company-ai-feedback--${bydaFilingFeedbackTone === 'error' ? 'error' : 'success'}`} style={{ marginTop: '12px' }}>
                    {bydaFilingFeedback}
                  </div>
                ) : null}
                {bydaDetailError ? (
                  <div className="company-ai-feedback company-ai-feedback--error" style={{ marginTop: '12px' }}>
                    {bydaDetailError}
                  </div>
                ) : null}
                {bydaYvwSewerError ? (
                  <div className="company-ai-feedback company-ai-feedback--error" style={{ marginTop: '12px' }}>
                    YVW sewer lookup failed: {bydaYvwSewerError}
                  </div>
                ) : null}
                {bydaDetail?.remoteSyncError ? (
                  <div className="company-ai-feedback company-ai-feedback--error" style={{ marginTop: '12px' }}>
                    Remote refresh warning: {bydaDetail.remoteSyncError.message}
                  </div>
                ) : null}
                {bydaDetail?.resultWarnings?.length ? (
                  <div className="company-ai-muted" style={{ marginTop: '12px' }}>
                    {bydaDetail.resultWarnings.join(' ')}
                  </div>
                ) : null}
                </div>

                <div className="company-ai-result-box">
                  <div className="company-ai-section-title" style={{ marginTop: 0 }}>Project folder target</div>
                  <div className="company-ai-muted">
                    {bydaDetail?.sharePointTarget?.folderPath || 'Project SharePoint target has not been resolved yet.'}
                  </div>
                  {bydaDetail?.sharePointTarget?.message ? (
                    <div className="company-ai-muted" style={{ marginTop: '8px' }}>
                      {bydaDetail.sharePointTarget.message}
                    </div>
                  ) : null}
                </div>
              </div>

              {bydaDetailLoading && bydaResultDocuments.length === 0 ? (
                <BydaResultFileList
                  documents={bydaResultDocuments}
                  loading={bydaDetailLoading}
                  saving={bydaFiling}
                  onPreviewDocument={setBydaPreviewDocument}
                  onSaveToSharePoint={handleBydaFileResults}
                  formatStatus={formatBydaStatus}
                  formatDateTime={formatDateTime}
                  loadingText="Loading BYDA result files..."
                />
              ) : bydaResultDocuments.length ? (
                <BydaResultFileList
                  documents={bydaResultDocuments}
                  saving={bydaFiling}
                  onPreviewDocument={setBydaPreviewDocument}
                  onSaveToSharePoint={handleBydaFileResults}
                  formatStatus={formatBydaStatus}
                  formatDateTime={formatDateTime}
                />
              ) : (
                <EmptyState
                  icon="📄"
                  title="No result files found yet"
                  description="This enquiry has not surfaced downloadable result files yet, or BYDA has only returned response metadata so far."
                />
              )}
            </div>
          )}
          <BydaResultPdfPreview document={bydaPreviewDocument} onClose={() => setBydaPreviewDocument(null)} />
          <BydaYvwSewerInfoDialog info={bydaYvwSewerInfo} onClose={() => setBydaYvwSewerInfo(null)} />
          </Card>
        </div>
      </div>
    </PageSection>
  );

  return (
    <div className={`company-ai-shell${activeWorkspaceTab === "pm-agent" ? " company-ai-shell--pm-agent" : ""}`}>
      <PageHeader
        title={activeWorkspaceTab === 'pm-agent' ? 'PM Agent' : 'Company AI Workspace'}
        subtitle={activeWorkspaceTab === 'pm-agent' ? undefined : 'Ask company questions, review read-only PMapp data, and manage the knowledge sources Company AI can use.'}
        itemCount={conversations.length}
        gradientColor="linear-gradient(135deg, #132c59 0%, #2251b4 54%, #4b6fbc 100%)"
        icon="🧠"
        searchEnabled={false}
        sortEnabled={false}
        userLabel={user?.userDetails || 'Unknown'}
        roleLabel={roleLabel}
      />

      <CompanyAiNavigationTabs
        currentPage="workspace"
        activeWorkspaceTab={activeWorkspaceTab}
        onWorkspaceTabChange={setActiveWorkspaceTab}
        userEmail={user?.userDetails}
        userRoles={user?.userRoles}
      />

      {activeWorkspaceTab === 'ask-ai' ? askAiTab : null}
      {activeWorkspaceTab === 'ai-search' ? aiSearchTab : null}
      {activeWorkspaceTab === 'pm-agent' ? <PmAgentPanel key={user?.userDetails || "signed-out"} /> : null}
      {activeWorkspaceTab === 'graph-rag' ? <GraphRagVisualizer canAccess={canManageKnowledgeSources} /> : null}
      {activeWorkspaceTab === 'finance' ? <FinancePortalPage /> : null}
      {activeWorkspaceTab === 'reconcile' ? <ReconcilePage userEmail={user?.userDetails} /> : null}
      {activeWorkspaceTab === 'dbyd' ? dbydTab : null}
      {activeWorkspaceTab === 'online-quote-files' ? onlineQuoteFilesTab : null}
      {activeWorkspaceTab === 'file-document' ? fileDocumentTab : null}
      {activeWorkspaceTab === 'project-files' ? projectFilesTab : null}
      {activeWorkspaceTab === 'ifc-models' ? ifcModelsTab : null}
    </div>
  );
}
